/**
 * ComparatorModal — generic add/edit modal for one `Comparator`
 * (Phase F.1a / GEN-2997). Reads and writes the canonical `Comparator`
 * shape directly; no per-slot (`mother` / `father`) shape touches this
 * component.
 *
 * F.1a scope (minimum surface):
 *   1. Relationship selection (full canonical vocabulary).
 *   2. Demographic identity: first / last name, DOB, MRN.
 *   3. Optional contact: email, phone (validated via the shared
 *      order-contact email/phone helpers).
 *   4. Primary-guardian toggle.
 *   5. Twin / multiple-birth sub-block (zygosity + birth order) revealed
 *      conditionally when the chosen relationship is `TWIN_SIBLING` or
 *      `MULTIPLE_BIRTH_SIBLING`. `setId` is **read-only display** —
 *      derived server-side in F.1b / F.1c.
 *
 * Phase F.1b-iv-a (GEN-2998) layered on top of F.1a:
 *   - Adds the three secondary-consent cards (incidental findings,
 *     NY sample retention, research-participation contact) behind a
 *     new optional `comparatorConsentConfig` prop. When the prop is
 *     omitted the consent block does not render, so all existing
 *     F.1a call sites and test suites are unchanged.
 *   - Consent block is gated to MOTHER / FATHER relationships with
 *     `isTestingComparator === true` AND at least one applicable
 *     section in the config.
 *
 * Phase F.1b-iv-b (GEN-2998 sub-task) layered on top of F.1b-iv-a:
 *   - Adds a "Clinical details" sub-block (phenotypic-trait dropdown +
 *     comparator-clinical-summary textarea) behind a new optional
 *     `enableClinicalFields` prop. When the prop is omitted the block
 *     does not render, so all existing F.1a / F.1b-iv-a callers and
 *     test suites stay untouched.
 *   - Persists to canonical `Comparator.phenotypicTrait` (string
 *     enum; `Unknown` collapses to `null` on save) and
 *     `Comparator.clinicalNote` (free-text string; trimmed/blank
 *     collapses to `null`). Clinical-note input enforces the same
 *     server-side allowlist (`isValidFreeText`) as legacy parents.
 *
 * Phase F.1b-iv-c (GEN-2998 sub-task) layered on top of F.1b-iv-b:
 *   - Adds a "Specimen" sub-block (inline `<RadioOption>` row of
 *     sample types — Whole Blood / Buccal Swab / Saliva) behind a
 *     new optional `enableSampleType` prop. The visible allowlist is
 *     supplied via the new optional `parentSampleTypes` prop, with a
 *     default of `['whole-blood', 'buccal-swab']` (no saliva option
 *     for parents).
 *   - Persists to canonical `Comparator.specimen.collectionType`
 *     (mapped via `mapSampleTypeToCollectionType`) and preserves a
 *     server-assigned `specimen.specimenId` through round-trip when
 *     `initialData.specimen.specimenId` is set. The save payload
 *     emits `specimen` only when the comparator actually carries
 *     specimen state — so the F.1a default path keeps emitting a
 *     payload without a `specimen` key.
 *   - Required-ness: when the block is enabled AND
 *     `isTestingComparator === true`, a sample type must be selected
 *     before save.
 *   - Auto-correction: if `parentSampleTypes` changes and a
 *     previously-selected sample type is no longer in the allowlist,
 *     the draft selection is cleared.
 *
 * Out of scope (deferred to later F.1b-iv-* slices):
 *   - HPO term editing
 *   - Patient-lookup integration (Epic FHIR / demographics)
 *   - Autosave (F.1b will plug `useAutosave` in when wired to a real page)
 *
 * Save semantics (Q5.A):
 *   - `onSave(comparator)` is invoked **once on submit**. Until then the
 *     modal owns the working draft.
 *   - `onClose` is called by the modal chrome (X / backdrop / Escape /
 *     Discard) without first calling onSave.
 *
 * Validation:
 *   - Required: relationship, firstName, lastName.
 *   - DOB optional in F.1a (proband chart already has DOB; comparator
 *     DOB is informational). When present, must parse as YYYY-MM-DD via
 *     the native date input.
 *   - Email / phone validated only when non-empty (i.e. when the field
 *     is not required).
 */

import { Button } from '@helix-ds/react'
import {
  Modal,
  ModalFooter,
  RadioOption,
  TextareaInput,
  TextInput,
  Toggle,
} from '@/components/common/Modal'
import { FieldMarker, FieldRequiredRail } from '@/components/common/FieldMarkers'
import { resolveFieldMarkerState } from '@/components/common/fieldMarkerState'
import { HPOSearchModal } from '@/components/common/HPOSearchModal'
import { AddressAutocomplete } from '@/components/common/AddressAutocomplete'
import { useAutosave } from '@/components/common/useAutosave'
import type { HPOSuggestion, HPOTerm } from '@/components/common/hpoTypes'
import { parseComparatorHpoTerms } from '@/ordering/comparatorHpoTerms'
import { useResolvedHpoTermNames } from '@/components/order/useResolvedHpoTermNames'
import {
  ComparatorParentConsentSections,
} from '@/components/order/ComparatorParentConsentSections'
import {
  type ComparatorParentConsentConfigSlice,
  comparatorConsentAnyApplicable,
  resolveConsentInteractionGates,
} from '@/components/order/comparatorParentConsentConfig'
import { CONSENT_REQUIRED_INTERACTION_ERROR } from '@/components/order/ProbandConsentSections'
import type { UiOrgAddressConfig } from '@/config/uiConfig'
import type { FamilyMember } from '@/components/order/OrderTypes'
import type {
  Comparator,
  ComparatorDemographic,
  ComparatorMultipleBirth,
  ComparatorSecondaryConsent,
  ComparatorSpecimen,
  FamilyMemberRelationship,
  KitShipmentRequested,
  Zygosity,
} from '@/ordering/canonicalOrder'
import {
  incidentalCanonicalToSelection,
  incidentalSelectionToCanonical,
} from '@/ordering/comparatorConsentMappers'
import {
  ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS,
  COMPARATOR_RELATIONSHIP_OPTIONS,
  formatComparatorRelationshipLabel,
  isMultipleBirthRelationship,
  isUniqueComparatorRelationship,
} from '@/ordering/comparatorRelationshipOptions'
import { parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import { requiredComparatorFields } from '@/ordering/requiredComparatorFields'
import { getPhenotypicTraitLabel, PhenotypicTrait, Sex } from '@/types/RequisitionFormTypes'
import type { IncidentalFindingsSelection } from '@/types/RequisitionFormTypes'
import {
  isValidOrderContactEmail,
  isValidOrderContactPhone,
  orderContactEmailError,
  orderContactPhoneError,
} from '@/utils/contactValidation'
import {
  EMAIL_MAX_LENGTH,
  freeTextError,
  nameError,
  NAME_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  NAME_TOO_LONG_MESSAGE,
  mrnError,
  MRN_MAX_LENGTH,
  MRN_TOO_LONG_MESSAGE,
  isWithinLength,
  type ClinicalTextBudget,
  RCIGM_COMPARATOR_CLINICAL_NOTE_BUDGET,
  RCIGM_COMPARATOR_CLINICAL_NOTE_TOO_LONG_MESSAGE,
  SHARED_CLINICAL_TEXT_HELPER,
  SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE,
} from '@/utils/freeTextValidation'
import {
  MINOR_COMPARATOR_CONTACT_HELPER,
  applyComparatorContactPolicy,
  comparatorContactPolicy,
  contactFieldsDisabled,
  contactHelperVisible,
} from '@/utils/comparatorMinorContact'
import { comparatorDobError, localTodayIso } from '@/utils/patientDob'
import { probandIncidentalFindingsLocksComparator } from '@/utils/comparatorIncidentalFindings'
import {
  getSampleTypeLabel,
  mapCollectionStringToSampleTypeStrict,
  mapSampleTypeToCollectionType,
  type SampleTypeOption,
} from '@/utils/orderFormUtils'
import {
  isEpicPatientDemographicsLookupAvailable,
  lookupPatientByDemographics,
} from '@/services/patientLookupByDemographics'
import { ArrowRight, ChevronDown, Close, Envelope, Phone, Plus, Search } from 'flowbite-react-icons/outline'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

/**
 * R5 (GEN side-nav comparator detail): which field groups the modal renders.
 * Lets one modal back the slim "Add Comparator" create step and the three
 * focused per-section editors (Demographics / Clinical history / Consent)
 * without duplicating the save/validation logic.
 *   - `full`         — every applicable block (default; preserves all callers).
 *   - `create`       — relationship + sample type + primary guardian only.
 *   - `demographics` — identity, contact, DOB/MRN, sample type, guardian, multiple-birth.
 *   - `clinical`     — phenotypic trait + clinical summary.
 *   - `consent`      — secondary-consent cards.
 */
export type ComparatorFieldGroup = 'full' | 'create' | 'demographics' | 'clinical' | 'consent'

export interface ComparatorModalProps {
  isOpen: boolean
  onClose: () => void
  /** Existing comparator when editing; `null`/`undefined` when adding. */
  initialData?: Comparator | null
  /**
   * R5: restrict the modal to a subset of field groups (see
   * `ComparatorFieldGroup`). Defaults to `'full'` so existing callers and
   * tests are unchanged.
   */
  fieldGroups?: ComparatorFieldGroup
  /** Invoked once when the user clicks Done with a valid draft. */
  onSave: (comparator: Comparator) => void
  /**
   * R5 section wizard: when provided, the primary footer button becomes a
   * "Continue to …" action instead of "Done". Clicking it validates and
   * saves the current section (via `onSave`) and then invokes `onContinue`
   * so the caller can advance to the next section editor. The modal stays
   * open. When omitted, the primary button is the usual "Done" (save + close).
   */
  continueTo?: { label: string; onContinue: () => void }
  /**
   * Optional pre-selected relationship for "add" flows (e.g. add-twin
   * button defaults to TWIN_SIBLING). Ignored when `initialData` is set.
   */
  defaultRelationship?: FamilyMemberRelationship
  /**
   * GEN-3152 Phase 2: override the relationship picker options. Defaults to
   * `ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS` (the canonical list minus PROBAND),
   * since PROBAND is the patient and is never a valid comparator relationship.
   * The "Add family member" flow passes
   * `NON_PARENT_COMPARATOR_RELATIONSHIP_OPTIONS` so the add surface can only
   * append non-parent relatives / legal guardians (never MOTHER / FATHER /
   * PROBAND, which have dedicated cards).
   */
  relationshipOptions?: typeof COMPARATOR_RELATIONSHIP_OPTIONS
  /**
   * Hide the primary-guardian toggle when irrelevant (e.g. proband row).
   */
  hidePrimaryGuardianToggle?: boolean
  /**
   * GEN-3152 Phase 1: when `true`, render a "Provides a sample (testing
   * comparator)" toggle bound to `isTestingComparator`. Lets the add flow
   * capture a non-testing person (e.g. a legal guardian who consents but is
   * not sequenced). When the toggle is off, the sample-type / specimen,
   * secondary-consent, and research-contact-channel requirements are relaxed
   * (they already gate on `isTestingComparator === true`). When omitted /
   * `false`, the toggle is hidden and `isTestingComparator` keeps its
   * caller/initial default — preserving the mother/father edit-pencil callers.
   */
  enableTestingToggle?: boolean
  /**
   * When true, show the "Request collection kit" toggle + destination/address
   * sub-form. Off by default so callers opt in (mirrors `enableTestingToggle`).
   */
  enableKitRequest?: boolean
  /**
   * Phase F.1b-iv-a (GEN-2998): when provided, the secondary-consent
   * cards (incidental findings / NY retention / research contact)
   * render below the demographic block — but only when the chosen
   * relationship is MOTHER / FATHER, `isTestingComparator === true`,
   * and at least one card is applicable in the config. When omitted
   * the consent block is absent (preserves F.1a callers).
   */
  comparatorConsentConfig?: ComparatorParentConsentConfigSlice
  /**
   * Phase F.1b-iv-b (GEN-2998): when `true`, the "Clinical details"
   * sub-block (phenotypic-trait dropdown + clinical-summary
   * textarea) renders below the demographic block. When omitted /
   * `false` the block is absent (preserves F.1a + F.1b-iv-a
   * callers). The block is purely additive — it never blocks save
   * unless the textarea contains disallowed characters.
   */
  enableClinicalFields?: boolean
  /**
   * Limit for the clinical summary. Rady checks each comparator on its own; other labs
   * count it against the order-wide pool. Defaults to the Rady per-comparator limit.
   */
  clinicalNoteBudget?: ClinicalTextBudget
  /**
   * Cell D — SeqOne HPO lock. When `true`, the HPO/phenotype editor inside the
   * "Clinical details" block (the phenotype list remove controls, the "Add
   * Phenotypes" button, and the HPO search modal) is suppressed so a persisted
   * member's HPO cannot be changed on a locked inflection_test order. The
   * phenotypic-trait dropdown and clinical-summary textarea stay editable
   * (rule 2: general updates remain allowed). Callers pass this only for
   * EXISTING/persisted comparators when `canEditExistingMemberHpo` is false;
   * newly-added comparators keep it `false` (rule 7). No-op when the clinical
   * block itself is not shown. Defaults to `false`.
   */
  suppressHpoEditing?: boolean
  /**
   * Phase F.1b-iv-c (GEN-2998): when `true`, the "Specimen" sub-block
   * (inline radio row of sample types) renders below the clinical
   * block. When omitted / `false` the block is absent and the save
   * payload remains free of any `specimen` key (preserves F.1a /
   * F.1b-iv-a / F.1b-iv-b callers).
   */
  enableSampleType?: boolean
  /**
   * Phase F.1b-iv-c (GEN-2998): allowed sample-type values for the
   * specimen radio group. Defaults to `['whole-blood', 'buccal-swab']`
   * when omitted (no saliva option for parents). Ignored when
   * `enableSampleType` is falsy.
   */
  parentSampleTypes?: ReadonlyArray<SampleTypeOption>
  /**
   * GEN-4933: organization mailing address (from `UiConfig.orgAddress`) used to
   * pre-fill the kit shipment address when the destination is Clinic / site
   * (the kit ships to the org's clinic, not a patient home). Fields stay fully
   * editable after pre-fill. When omitted, selecting Clinic / site leaves the
   * address fields as-is.
   */
  labAddress?: UiOrgAddressConfig
  /**
   * Phase F.1b-vii-a (GEN-2998 sub-task): when `true`, the MRN field
   * becomes required — the label switches to "MRN" (with a required
   * indicator), the placeholder switches from "Enter MRN (optional)"
   * to "Enter MRN", and save is blocked with "Medical record number
   * is required" until the user fills the field. When omitted /
   * `false`, MRN stays optional (preserves F.1a/F.1b-iv-a/b/c
   * callers).
   */
  mrnRequired?: boolean
  /**
   * Phase F.1b-vii-a (GEN-2998 sub-task): when `true`, the F.1b-iv-c
   * specimen / sample-type block renders the currently-selected
   * sample type as static read-only text instead of an interactive
   * radio group. The required-sample-type validation is also
   * suppressed under lock (a caller-supplied value cannot be
   * corrected by the user). Ignored when `enableSampleType` is
   * falsy. When omitted / `false`, the radio group remains
   * interactive (preserves F.1b-iv-c callers).
   */
  lockSampleType?: boolean
  /**
   * Phase F.1b-vii-b (GEN-2998 sub-task): proband-side research-contact
   * opt-in flag. Together with `patientResearchContactAppliesToThisParent`
   * forces email-or-phone presence on the parent row so the lab has a
   * way to reach the parent for proband research contact. When
   * omitted / `false`, the contact-channel gating only fires from the
   * parent's own `researchContactConsent` toggle.
   */
  patientResearchContactOptIn?: boolean
  /**
   * Phase F.1b-vii-b (GEN-2998 sub-task): when `true` (alongside
   * `patientResearchContactOptIn=true` and `isTestingComparator=true`),
   * the email-or-phone validation fires on this comparator row.
   * Callers compute applicability via
   * `FormCompletionChecker.canonicalComparatorRowObligatedForProbandResearchContact`
   * for the corresponding parent slot. When omitted / `false`, the
   * patient-side path of `contactChannelRequired` is inert.
   */
  patientResearchContactAppliesToThisParent?: boolean
  /**
   * When `true`, this comparator is the primary guardian AND the proband opted
   * into research contact, so it must supply a contact channel (email or phone)
   * regardless of relationship or testing status — the primary guardian owns
   * the proband's research-contact channel for CareBus. Independent of
   * `patientResearchContactAppliesToThisParent`, which is parent-slot-scoped and
   * gated on `isTestingComparator`.
   */
  probandResearchContactPrimaryGuardian?: boolean
  /**
   * GEN-5654: the platform collects this comparator's consent to test (resolved
   * by the caller via `resolveComparatorPlatformCollects`). A testing adult then
   * needs email or phone; a testing minor needs guardian contact on the order
   * (`guardianContactOnFile`). When omitted / `false`, contact stays optional.
   */
  platformCollectsConsentToTest?: boolean
  /** GEN-5654: the order already carries a guardian email/phone (see `orderHasGuardianContact`). */
  guardianContactOnFile?: boolean
  /**
   * GEN-5654: opens the existing Guardian Contact modal. Invoked when a save is
   * blocked because a minor comparator needs guardian contact, and by the inline
   * "Add guardian contact" action.
   */
  onRequestGuardianContact?: () => void
  /** GEN-5654: the caller covers this modal with the Guardian Contact modal; hide it meanwhile. */
  coveredByGuardianModal?: boolean
  /**
   * GEN-3484: the proband's incidental-findings selection. When `'opt-out'`,
   * a comparator may not opt in to incidental findings — the modal's consent
   * block locks the incidental toggle to opt-out and shows the lock message,
   * mirroring the legacy (non-side-nav) behavior. The Redux `mergePatient`
   * reducer independently forces every comparator to `false` on proband
   * opt-out; this prop only drives the in-modal visual lock. When omitted /
   * `null`, the toggle is interactive.
   */
  probandIncidentalFindingsSelection?: IncidentalFindingsSelection | null
  /**
   * Phase 2c: when `true`, date of birth (and phenotypic trait) become
   * required for a testing comparator, matching the page-level completeness
   * rule in `FormCompletionChecker.isParentDataFullyComplete`. Wired by the
   * parent-modal mounts so a parent the modal accepts is also "complete" per
   * the page. When omitted / `false`, DOB stays optional (preserves all
   * earlier callers). Phenotypic trait defaults to `Unknown`, which the page
   * treats as complete, so the trait requirement is effectively always
   * satisfied; it is included for parity with the page contract.
   */
  requireClinicalIdentity?: boolean
  /**
   * When `true`, the modal debounce-autosaves the in-progress draft via
   * `onSave` (only while the visible field group is valid) and renders the
   * standard "Saved … / Discard Changes / Done" footer — matching the other
   * in-progress section modals (Clinical Summary, Indications, …). Discard
   * reverts canonical to the open-time baseline. When omitted / `false`, the
   * modal keeps its manual "Cancel / Done" footer and `onSave` only fires on
   * Done (preserves all add/create + OrderStart callers).
   */
  autosave?: boolean
  /**
   * Legacy (non-side-nav) fixed biological-parent behavior. When `true` (the
   * OrderStart / OrderInProgress mother/father edit-pencil mounts):
   *   - the Relationship select is locked for a MOTHER / FATHER row, and
   *   - sex assigned at birth defaults from the relationship (MOTHER → female,
   *     FATHER → male) when unset.
   * The R5 side-nav editors and add flows omit this so they keep the
   * relationship editable and never auto-fill sex.
   */
  legacyFixedParentDefaults?: boolean
  /**
   * Epic launch flow only: enables the EHR patient-demographics MRN lookup in
   * the demographics field group. When true (and `epicState` resolves to a
   * live Epic session), the MRN field gains a search affordance and the modal
   * auto-queries `POST /fhir/patient/search` once first/last/DOB are populated
   * for a testing comparator. Standalone (web) launches omit this so no lookup
   * UI or network calls occur. Ignored unless the demographics group is shown.
   */
  isEpicFlow?: boolean
  /**
   * Epic SMART OAuth state key used to locate the stored Epic session for the
   * patient-demographics lookup (`POST /fhir/patient/search`). Required for the
   * lookup to run; when empty/missing the lookup reports "not configured".
   */
  epicState?: string
  /**
   * Guardian-contact demographics used to pre-fill an EMPTY comparator field
   * (name / DOB / email / phone) when this comparator's relationship both
   * matches the guardian's `relationshipToPatient` AND is a unique (1-of-1)
   * relationship — so a guardian who is also, say, the mother isn't re-typed.
   *
   * Only applied on open, only to fields the initial draft leaves blank, and
   * never overwrites a value the user already entered. Matching is by canonical
   * UPPER_CASE relationship (legacy lowercase tolerated). When omitted, no
   * pre-fill occurs (preserves every existing caller).
   */
  guardianPrefill?: GuardianPrefill | null
}

interface DraftState {
  id: string | null
  /**
   * Stable demographic identity from the server; carried through the modal
   * round-trip so the backend can find-and-update the existing demographic
   * row instead of inserting a new (family_member, demographic) pair when
   * a name/email/phone/DOB/MRN field changes.
   */
  demographicId: string | null
  relationship: FamilyMemberRelationship | ''
  firstName: string
  lastName: string
  dateOfBirth: string
  /** Sex assigned at birth (canonical `Sex` value; `Unknown`/'' = not chosen). Required like the proband's. */
  sex: Sex
  mrn: string
  email: string
  phone: string
  isPrimaryGuardian: boolean
  isTestingComparator: boolean
  multipleBirth: ComparatorMultipleBirth | null
  /** F.1b-iv-a — canonical tri-state: true=opt-in, false=opt-out, null=not chosen. */
  includeIncidentalFindings: boolean | null
  /** F.1b-iv-a — canonical boolean toggle; null = unchecked (default per disclosure). */
  newYorkSampleRetentionOptIn: boolean | null
  /** F.1b-iv-a — canonical boolean toggle; null = not selected. */
  researchContactConsent: boolean | null
  /** F.1b-iv-b — `Unknown` collapses to `null` on save (matches legacy parent serialisation). */
  phenotypicTrait: PhenotypicTrait
  /** F.1b-iv-b — trimmed/blank collapses to `null` on save. */
  clinicalNote: string
  /** R5 — comparator HPO phenotype list (Key / Additional / Pertinent Negative). */
  hpoTerms: HPOTerm[]
  /** F.1b-iv-c — UI-facing sample type; `null` = no selection. Maps to specimen.collectionType on save. */
  sampleType: SampleTypeOption | null
  /** F.1b-iv-c — server-assigned specimenId carried through round-trip without UI exposure. */
  existingSpecimenId: string | null
  /** Kit shipment request — when on, a collection kit is shipped to the recipient. */
  requestKit: boolean
  /** Kit destination: 'HOME' | 'CLINIC' (''=not chosen). */
  kitLocation: string
  kitStreet: string
  kitStreet2: string
  kitCity: string
  kitState: string
  kitZip: string
}

const DEFAULT_PARENT_SAMPLE_TYPES: ReadonlyArray<SampleTypeOption> = ['whole-blood', 'buccal-swab']

const KIT_LOCATION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Select destination' },
  { value: 'HOME', label: 'Patient home (direct to patient)' },
  { value: 'CLINIC', label: 'Clinic / site (direct to business)' },
]

/**
 * GEN-4933: for a Whole Blood sample the kit may only ship to the clinic / site
 * (a blood draw is performed on-site). Both "Patient home" AND the empty
 * "Select destination" placeholder are dropped, leaving Clinic / site as the
 * sole (force-selected) option — so reselecting an empty value can never re-run
 * the force-Clinic pre-fill and wipe a user's edited clinic address. Non-blood
 * sample types keep the full {@link KIT_LOCATION_OPTIONS}.
 */
const WHOLE_BLOOD_KIT_LOCATION_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  KIT_LOCATION_OPTIONS.filter((o) => o.value === 'CLINIC')

const PHENOTYPIC_TRAIT_OPTIONS: ReadonlyArray<{ value: PhenotypicTrait; label: string }> = [
  PhenotypicTrait.Affected,
  PhenotypicTrait.Unaffected,
  PhenotypicTrait.PartiallyAffected,
  PhenotypicTrait.Unknown,
].map((value) => ({ value, label: getPhenotypicTraitLabel(value) }))

function normalizePhenotypicTraitForDraft(value: unknown): PhenotypicTrait {
  if (
    value === PhenotypicTrait.Affected ||
    value === PhenotypicTrait.Unaffected ||
    value === PhenotypicTrait.PartiallyAffected ||
    value === PhenotypicTrait.Unknown
  ) {
    return value
  }
  return PhenotypicTrait.Unknown
}

/** Coerce a stored `demographic.sex` string into the `Sex` enum (defaults to Unknown). */
function normalizeSexForDraft(value: unknown): Sex {
  if (value === Sex.Male || value === Sex.Female || value === Sex.Ambiguous) return value
  return Sex.Unknown
}

/**
 * Default "sex assigned at birth" implied by a relationship, used to pre-fill
 * the draft when the stored value is unset (still fully user-editable — a
 * convenience pre-fill, not a lock; intersex / affirming-care overrides are
 * expected and allowed). Covers every gendered relationship in
 * {@link API_SUPPORTED_RELATIONSHIP_VALUES}:
 *
 *   Female → MOTHER, AUNT_MATERNAL, AUNT_PATERNAL, GRANDMOTHER_MATERNAL,
 *            GRANDMOTHER_PATERNAL
 *   Male   → FATHER, UNCLE_MATERNAL, UNCLE_PATERNAL, GRANDFATHER_MATERNAL,
 *            GRANDFATHER_PATERNAL
 *
 * Returns `null` for SIBLING and any non-gendered / indeterminate relationship
 * (no default). Applies to every flow (legacy parent pencil + R5 /
 * add-family-member).
 */
const SEX_BY_RELATIONSHIP: Partial<Record<FamilyMemberRelationship, Sex>> = {
  MOTHER: Sex.Female,
  AUNT_MATERNAL: Sex.Female,
  AUNT_PATERNAL: Sex.Female,
  GRANDMOTHER_MATERNAL: Sex.Female,
  GRANDMOTHER_PATERNAL: Sex.Female,
  FATHER: Sex.Male,
  UNCLE_MATERNAL: Sex.Male,
  UNCLE_PATERNAL: Sex.Male,
  GRANDFATHER_MATERNAL: Sex.Male,
  GRANDFATHER_PATERNAL: Sex.Male,
}

function defaultSexForRelationship(relationship: FamilyMemberRelationship | ''): Sex | null {
  if (relationship === '') return null
  return SEX_BY_RELATIONSHIP[relationship] ?? null
}

const SEX_OPTIONS: ReadonlyArray<{ value: Sex; label: string }> = [
  { value: Sex.Male, label: 'Male' },
  { value: Sex.Female, label: 'Female' },
  { value: Sex.Ambiguous, label: 'Ambiguous' },
]

const ZYGOSITY_OPTIONS: ReadonlyArray<{ value: Zygosity; label: string }> = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'MONOZYGOTIC', label: 'Monozygotic (identical)' },
  { value: 'DIZYGOTIC', label: 'Dizygotic (fraternal)' },
  { value: 'NOT_APPLICABLE', label: 'Not applicable' },
]

function strOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value : ''
}

function nullIfEmpty(value: string): string | null {
  const t = value.trim()
  return t === '' ? null : t
}

function emptyMultipleBirth(): ComparatorMultipleBirth {
  return { setId: '', birthOrder: null, zygosity: 'UNKNOWN' }
}

/**
 * Guardian-contact subset used to pre-fill a matching comparator's empty
 * demographic fields. Mirrors {@link ComparatorModalProps.guardianPrefill}.
 */
export interface GuardianPrefill {
  relationshipToPatient?: string | null
  firstName?: string | null
  lastName?: string | null
  dateOfBirth?: string | null
  email?: string | null
  phone?: string | null
}

/**
 * Normalize a relationship literal for guardian ↔ comparator matching:
 * trimmed, upper-cased, and legacy lowercase tokens mapped to canonical
 * (`bio_mother` → `MOTHER`). Returns `''` for empty/unknown input so a blank
 * guardian relationship never matches a blank comparator relationship.
 */
function normalizeRelationshipForMatch(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''
  const upper = raw.toUpperCase()
  switch (upper) {
    case 'BIO_MOTHER':
      return 'MOTHER'
    case 'BIO_FATHER':
      return 'FATHER'
    default:
      return upper
  }
}

/**
 * Whether a guardian contact should pre-fill a comparator with the given
 * relationship: a guardian exists, the relationship is non-empty and unique
 * (only one family member can hold it — e.g. MOTHER), and the guardian's
 * `relationshipToPatient` matches it (canonical, legacy-lowercase tolerant).
 * Shared by the open-time draft build and the on-select pre-fill so both fire
 * on exactly the same condition.
 */
function guardianMatchesRelationship(
  guardianPrefill: GuardianPrefill | null | undefined,
  relationship: FamilyMemberRelationship | '' | null | undefined
): boolean {
  return (
    guardianPrefill != null &&
    typeof relationship === 'string' &&
    relationship !== '' &&
    isUniqueComparatorRelationship(relationship) &&
    normalizeRelationshipForMatch(guardianPrefill.relationshipToPatient) === normalizeRelationshipForMatch(relationship)
  )
}

function buildInitialDraft(
  initialData: Comparator | null | undefined,
  defaultRelationship: FamilyMemberRelationship | undefined,
  guardianPrefill?: GuardianPrefill | null
): DraftState {
  const demo: ComparatorDemographic = initialData?.demographic ?? {}
  const relationship: DraftState['relationship'] =
    initialData?.relationship ?? defaultRelationship ?? ''
  const consent: ComparatorSecondaryConsent = initialData?.secondaryConsent ?? {}
  const kit: KitShipmentRequested = initialData?.kitShipmentRequested ?? {}
  const kitAddr = kit.shipmentAddress ?? {}
  // UX-056: "sex assigned at birth" defaults from the relationship whenever the
  // relationship implies one (see `defaultSexForRelationship`) and the stored
  // value is unset — in every flow (legacy parent pencil + R5 / add-family-
  // member). An explicitly stored value always wins; the pre-fill stays fully
  // user-editable.
  const normalizedSex = normalizeSexForDraft(demo.sex)
  const sex =
    normalizedSex === Sex.Unknown
      ? (defaultSexForRelationship(relationship) ?? normalizedSex)
      : normalizedSex

  // Guardian → comparator pre-fill: when this comparator's relationship matches
  // the guardian's `relationshipToPatient` AND that relationship is unique
  // (only one family member can hold it — e.g. MOTHER), reuse the guardian's
  // demographics so the provider doesn't re-type the same person. Empty-only:
  // any value already on the comparator (stored or typed) is never overwritten.
  const guardianMatches = guardianMatchesRelationship(guardianPrefill, relationship)
  const prefillIfEmpty = (stored: string | null | undefined, guardianValue: string | null | undefined): string => {
    const base = strOrEmpty(stored)
    if (base.trim() !== '') return base
    return guardianMatches ? strOrEmpty(guardianValue) : base
  }

  return {
    id: initialData?.id ?? null,
    demographicId: typeof demo.demographicId === 'string' && demo.demographicId.trim() !== ''
      ? demo.demographicId
      : null,
    relationship,
    firstName: prefillIfEmpty(demo.firstName, guardianPrefill?.firstName),
    lastName: prefillIfEmpty(demo.lastName, guardianPrefill?.lastName),
    dateOfBirth: prefillIfEmpty(demo.dateOfBirth, guardianPrefill?.dateOfBirth),
    sex,
    mrn: strOrEmpty(demo.mrn),
    email: prefillIfEmpty(demo.email, guardianPrefill?.email),
    phone: prefillIfEmpty(demo.phone, guardianPrefill?.phone),
    isPrimaryGuardian: initialData?.isPrimaryGuardian === true,
    isTestingComparator: initialData?.isTestingComparator !== false,
    multipleBirth: initialData?.multipleBirth ?? null,
    includeIncidentalFindings:
      consent.includeIncidentalFindings === true || consent.includeIncidentalFindings === false
        ? consent.includeIncidentalFindings
        : null,
    newYorkSampleRetentionOptIn:
      consent.newYorkSampleRetentionOptIn === true || consent.newYorkSampleRetentionOptIn === false
        ? consent.newYorkSampleRetentionOptIn
        : null,
    researchContactConsent:
      consent.researchContactConsent === true || consent.researchContactConsent === false
        ? consent.researchContactConsent
        : null,
    phenotypicTrait: normalizePhenotypicTraitForDraft(initialData?.phenotypicTrait),
    clinicalNote: strOrEmpty(initialData?.clinicalNote),
    hpoTerms: parseComparatorHpoTerms(initialData?.hpoTerms),
    sampleType: mapCollectionStringToSampleTypeStrict(initialData?.specimen?.collectionType ?? null),
    existingSpecimenId:
      typeof initialData?.specimen?.specimenId === 'string' &&
      initialData.specimen.specimenId.trim() !== ''
        ? initialData.specimen.specimenId
        : null,
    requestKit: initialData?.kitShipmentRequested != null,
    kitLocation: strOrEmpty(kit.location),
    kitStreet: strOrEmpty(kitAddr.street),
    kitStreet2: strOrEmpty(kitAddr.street2),
    kitCity: strOrEmpty(kitAddr.city),
    kitState: strOrEmpty(kitAddr.state),
    kitZip: strOrEmpty(kitAddr.zip),
  }
}

interface FieldErrors {
  firstName?: string
  lastName?: string
  sex?: string
  email?: string
  phone?: string
  relationship?: string
  clinicalNote?: string
  sampleType?: string
  mrn?: string
  contactChannel?: string
  /** GEN-5654: synthetic — minor comparator needs guardian contact on the order. */
  guardianContact?: string
  dateOfBirth?: string
  kitLocation?: string
  kitStreet?: string
  kitCity?: string
  kitState?: string
  kitZip?: string
  /**
   * Synthetic key for an unanswered `requireInteraction` consent section
   * (incidental / NY / research). Not tied to a single input — the inline
   * error renders on the offending row inside <ComparatorParentConsentSections>;
   * this key exists so a blocked save flips `showErrors` and the save banner
   * lists the section.
   */
  consent?: string
}

/**
 * Human-readable labels for the save-blocked banner (GEN-2998). When the
 * user clicks Done with invalid/missing fields, the banner lists these
 * names so the failure is never silent.
 */
const FIELD_ERROR_LABELS: Record<keyof FieldErrors, string> = {
  relationship: 'Biological Relative',
  firstName: 'First name',
  lastName: 'Last name',
  sex: 'Sex assigned at birth',
  email: 'Email',
  phone: 'Phone',
  clinicalNote: 'Clinical notes',
  sampleType: 'Sample type',
  mrn: 'Medical record number',
  contactChannel: 'Contact channel (email or phone)',
  guardianContact: 'Guardian contact (email or phone)',
  dateOfBirth: 'Date of birth',
  kitLocation: 'Kit destination',
  kitStreet: 'Street address',
  kitCity: 'City',
  kitState: 'State',
  kitZip: 'ZIP',
  consent: 'Additional Options',
}

/**
 * Which validation errors can block save for each field group. A blocked save
 * must only consider errors for fields the group actually renders — e.g. the
 * slim `create` step hides the name fields, so a missing first name can never
 * block it.
 */
const ERROR_KEYS_BY_GROUP: Record<ComparatorFieldGroup, ReadonlyArray<keyof FieldErrors>> = {
  full: [
    'relationship',
    'firstName',
    'lastName',
    'sex',
    'email',
    'phone',
    'clinicalNote',
    'sampleType',
    'mrn',
    'contactChannel',
    'guardianContact',
    'dateOfBirth',
    'kitLocation',
    'kitStreet',
    'kitCity',
    'kitState',
    'kitZip',
    'consent',
  ],
  create: ['relationship', 'sampleType', 'kitLocation', 'kitStreet', 'kitCity', 'kitState', 'kitZip'],
  demographics: [
    'relationship',
    'firstName',
    'lastName',
    'sex',
    'email',
    'phone',
    'sampleType',
    'mrn',
    'contactChannel',
    'guardianContact',
    'dateOfBirth',
    'kitLocation',
    'kitStreet',
    'kitCity',
    'kitState',
    'kitZip',
  ],
  clinical: ['clinicalNote'],
  consent: ['consent'],
}

interface ComputeErrorsOptions {
  clinicalNoteBudget?: ClinicalTextBudget
  /** F.1b-iv-c — when true, sample-type is required for testing comparators. */
  enableSampleType?: boolean
  /** F.1b-vii-a — when true, MRN is required. */
  mrnRequired?: boolean
  /** F.1b-vii-a — when true, sample-type required-validation is skipped (caller-supplied value). */
  lockSampleType?: boolean
  /** F.1b-vii-b — proband-side research-contact opt-in (forces contact channel when also `appliesToThisParent`). */
  patientResearchContactOptIn?: boolean
  /** F.1b-vii-b — whether the patient's research-contact obligation applies to this row. */
  patientResearchContactAppliesToThisParent?: boolean
  /**
   * When true (alongside the draft being the primary guardian), the proband's
   * research-contact opt-in obligates THIS comparator to supply a contact
   * channel regardless of relationship / testing status.
   */
  probandResearchContactPrimaryGuardian?: boolean
  /** GEN-5654 — platform collects consent to test for this comparator. */
  platformCollectsConsentToTest?: boolean
  /** GEN-5654 — order already has guardian email/phone. */
  guardianContactOnFile?: boolean
  /** Phase 2c — when true, a testing comparator must provide clinical identity (DOB). */
  requireClinicalIdentity?: boolean
}

function clinicalNoteBudgetCount(clinicalNote: string, budget: ClinicalTextBudget): number {
  return budget.usedElsewhere + clinicalNote.trim().length
}

function minorNeedsGuardianContact(
  draft: DraftState,
  options: Pick<ComputeErrorsOptions, 'platformCollectsConsentToTest' | 'guardianContactOnFile'>
): boolean {
  return (
    options.platformCollectsConsentToTest === true &&
    options.guardianContactOnFile !== true &&
    draft.isTestingComparator &&
    comparatorContactPolicy(draft.dateOfBirth) === 'blockedMinor'
  )
}

function computeErrors(draft: DraftState, options: ComputeErrorsOptions = {}): FieldErrors {
  const errs: FieldErrors = {}
  // F.1b-vii-b: a research-contact obligation applies when the comparator's
  // own toggle is on OR the proband-side opt-in is projected onto this row.
  const parentResearchOptIn = draft.researchContactConsent === true
  const patientResearchOnThisRow =
    options.patientResearchContactOptIn === true &&
    options.patientResearchContactAppliesToThisParent === true
  // A primary guardian owns the proband's research-contact channel, so once the
  // proband opts in, THIS row must supply email/phone regardless of testing.
  const primaryGuardianResearchContactRequired =
    options.probandResearchContactPrimaryGuardian === true && draft.isPrimaryGuardian === true
  // Single source of truth for which fields this comparator must fill out;
  // format validation (email/phone/free-text) stays inline below.
  const required = requiredComparatorFields({
    isTesting: draft.isTestingComparator,
    enableSampleType: options.enableSampleType,
    lockSampleType: options.lockSampleType,
    mrnRequired: options.mrnRequired,
    researchContactRequired: parentResearchOptIn || patientResearchOnThisRow,
    primaryGuardianResearchContactRequired,
    platformCollectsConsentToTest: options.platformCollectsConsentToTest === true,
    requiresClinicalIdentity: options.requireClinicalIdentity && draft.isTestingComparator,
    dateOfBirth: draft.dateOfBirth,
  })

  if (required.has('relationship') && !draft.relationship) errs.relationship = 'Select a relationship'
  // Phase 2c: DOB matches the page completeness rule. Phenotypic trait is also
  // in the required set but defaults to `Unknown` (which the page accepts), so
  // it can never be empty here — no explicit check is needed.
  if (required.has('dateOfBirth') && !draft.dateOfBirth.trim()) {
    errs.dateOfBirth = 'Enter a date of birth'
  } else if (draft.dateOfBirth.trim()) {
    // When a DOB is entered (required or not), enforce the same guardrails as the
    // proband: it must be a real date, not in the future, and a plausible age.
    const dobErr = comparatorDobError(draft.dateOfBirth)
    if (dobErr) errs.dateOfBirth = dobErr
  }
  if (required.has('firstName') && !draft.firstName.trim()) errs.firstName = 'Enter a first name'
  else {
    const firstNameErr =
      nameError(draft.firstName) ??
      (!isWithinLength(draft.firstName, NAME_MAX_LENGTH) ? NAME_TOO_LONG_MESSAGE : undefined)
    if (firstNameErr) errs.firstName = firstNameErr
  }
  if (required.has('lastName') && !draft.lastName.trim()) errs.lastName = 'Enter a last name'
  else {
    const lastNameErr =
      nameError(draft.lastName) ??
      (!isWithinLength(draft.lastName, NAME_MAX_LENGTH) ? NAME_TOO_LONG_MESSAGE : undefined)
    if (lastNameErr) errs.lastName = lastNameErr
  }
  if (required.has('sex') && !draft.sex) errs.sex = 'Select sex assigned at birth'
  const noteBudget = options.clinicalNoteBudget ?? RCIGM_COMPARATOR_CLINICAL_NOTE_BUDGET
  const contactOptions = { rady: noteBudget.kind === 'rcigm' }
  if (draft.email.trim() && !isValidOrderContactEmail(draft.email, contactOptions)) {
    errs.email = orderContactEmailError(draft.email, contactOptions)
  }
  if (draft.phone.trim() && !isValidOrderContactPhone(draft.phone, contactOptions)) {
    errs.phone = orderContactPhoneError(draft.phone, contactOptions)
  }
  // F.1b-iv-b: clinical note must satisfy the server-side free-text allowlist
  // when it contains content. Empty/blank is always valid (field is optional).
  const clinicalNoteErr =
    freeTextError(draft.clinicalNote) ??
    (clinicalNoteBudgetCount(draft.clinicalNote, noteBudget) > noteBudget.limit
      ? noteBudget.kind === 'shared'
        ? SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE
        : RCIGM_COMPARATOR_CLINICAL_NOTE_TOO_LONG_MESSAGE
      : undefined)
  if (clinicalNoteErr) errs.clinicalNote = clinicalNoteErr
  if (required.has('sampleType') && draft.sampleType === null) {
    errs.sampleType = 'Select a sample type'
  }
  if (required.has('mrn') && !draft.mrn.trim()) {
    errs.mrn = 'Enter an MRN'
  } else {
    const mrnFormatErr =
      mrnError(draft.mrn) ?? (!isWithinLength(draft.mrn, MRN_MAX_LENGTH) ? MRN_TOO_LONG_MESSAGE : undefined)
    if (mrnFormatErr) errs.mrn = mrnFormatErr
  }
  if (required.has('contactChannel') && !draft.email.trim() && !draft.phone.trim()) {
    errs.contactChannel =
      options.platformCollectsConsentToTest === true
        ? 'Enter an email or phone number so consent to test can be collected'
        : 'Enter an email or phone number for research contact'
  }
  // GEN-5654: a minor's own contact is never captured (GEN-5651); when the platform
  // collects consent, the order's guardian contact stands in for it.
  if (minorNeedsGuardianContact(draft, options)) {
    errs.guardianContact = 'Enter a guardian email or phone number so consent to test can be collected'
  }
  // Kit request: when the toggle is on (only reachable for a testing comparator),
  // a destination is required so we never persist an inert "kit requested" state.
  if (draft.isTestingComparator && draft.requestKit && !draft.kitLocation.trim()) {
    errs.kitLocation = 'Select a kit destination'
  }
  // GEN-4734: when a kit ships to a home address, the shipping address is
  // mandatory — except "Apt / suite" (`kitStreet2`), which stays optional.
  if (draft.isTestingComparator && draft.requestKit && draft.kitLocation === 'HOME') {
    if (!draft.kitStreet.trim()) errs.kitStreet = 'Enter a street address'
    if (!draft.kitCity.trim()) errs.kitCity = 'Enter a city'
    if (!draft.kitState.trim()) errs.kitState = 'Enter a state'
    if (!draft.kitZip.trim()) errs.kitZip = 'Enter a ZIP code'
  }
  return errs
}

function buildKitShipmentRequested(draft: DraftState): KitShipmentRequested | null | undefined {
  // A non-testing comparator can never carry a collection kit (no sample ships).
  // Emit an explicit `null` so the merge/back-fill CLEARS any prior kit block
  // rather than leaving a hidden/orphaned kit that the emitter would still ship.
  if (!draft.isTestingComparator) return draft.requestKit || draft.kitLocation ? null : undefined
  if (!draft.requestKit) return undefined
  const addrFields = {
    street: nullIfEmpty(draft.kitStreet),
    street2: nullIfEmpty(draft.kitStreet2),
    city: nullIfEmpty(draft.kitCity),
    state: nullIfEmpty(draft.kitState),
    zip: nullIfEmpty(draft.kitZip),
  }
  const hasAddr = Object.values(addrFields).some((v) => v != null)
  const kit: KitShipmentRequested = { location: nullIfEmpty(draft.kitLocation) }
  if (hasAddr) {
    // 'RESIDENTIAL' for a home address, 'HOSPITAL' for a clinic destination.
    kit.shipmentAddress = {
      type: draft.kitLocation === 'CLINIC' ? 'HOSPITAL' : 'RESIDENTIAL',
      ...addrFields,
    }
  }
  return kit
}

function draftToComparator(draft: DraftState): Comparator {
  // Save boundary: draft commits already apply policy; keep this belt-and-suspenders
  // so a raw DraftState still strips blocked minors before export.
  const cleared = applyComparatorContactPolicy(draft)
  const demographic: ComparatorDemographic = {
    firstName: nullIfEmpty(cleared.firstName),
    lastName: nullIfEmpty(cleared.lastName),
    dateOfBirth: nullIfEmpty(cleared.dateOfBirth),
    sex: cleared.sex ? cleared.sex : null,
    mrn: nullIfEmpty(cleared.mrn),
    email: nullIfEmpty(cleared.email),
    phone: nullIfEmpty(cleared.phone),
  }
  // Preserve stable demographic identity across the round-trip so the
  // backend updates the existing row rather than inserting a new
  // (family_member, demographic) pair on name/email/phone/DOB/MRN edits.
  if (cleared.demographicId) demographic.demographicId = cleared.demographicId
  const multipleBirth =
    cleared.multipleBirth && isMultipleBirthRelationship(cleared.relationship)
      ? cleared.multipleBirth
      : null
  // F.1b-iv-a: serialise `secondaryConsent` only when at least one
  // tri-state is non-null. Preserves round-trip stability — a modal
  // opened without the consent block (or with all three controls
  // untouched) re-emits the same payload it received.
  const hasConsent =
    cleared.includeIncidentalFindings !== null ||
    cleared.newYorkSampleRetentionOptIn !== null ||
    cleared.researchContactConsent !== null
  const secondaryConsent: ComparatorSecondaryConsent | undefined = hasConsent
    ? {
        includeIncidentalFindings: cleared.includeIncidentalFindings,
        newYorkSampleRetentionOptIn: cleared.newYorkSampleRetentionOptIn,
        researchContactConsent: cleared.researchContactConsent,
      }
    : undefined
  // F.1b-iv-b: collapse `Unknown` trait and blank/whitespace notes to
  // `null` so the saved payload matches the legacy parent
  // serialisation (`canonicalOrder.ts` does the same on hydration —
  // see the `applyComparatorToParentSlot` path).
  const trimmedNote = cleared.clinicalNote.trim()
  const phenotypicTraitOut: string | null =
    cleared.phenotypicTrait === PhenotypicTrait.Unknown ? null : cleared.phenotypicTrait
  const clinicalNoteOut: string | null = trimmedNote === '' ? null : trimmedNote
  // F.1b-iv-c: emit `specimen` only when the comparator actually
  // carries specimen state — either a chosen `sampleType` or a
  // server-assigned `specimenId` round-tripping through the draft.
  // This preserves F.1a/F.1b-iv-a/F.1b-iv-b parity (no spurious
  // `{ specimenId: null, collectionType: null }` payload).
  const hasSpecimen = cleared.existingSpecimenId !== null || cleared.sampleType !== null
  const specimen: ComparatorSpecimen | undefined = hasSpecimen
    ? {
        specimenId: cleared.existingSpecimenId,
        collectionType: cleared.sampleType !== null ? mapSampleTypeToCollectionType(cleared.sampleType) : null,
      }
    : undefined
  // Kit shipment request: emit `kitShipmentRequested` only when the toggle is on.
  // Its presence is what makes provider-api emit the kit_shipment.requested event.
  // The address is included only when at least one address field is populated.
  const kitShipmentRequested = buildKitShipmentRequested(cleared)
  const out: Comparator = {
    id: cleared.id ?? undefined,
    relationship: cleared.relationship as FamilyMemberRelationship,
    demographic,
    isPrimaryGuardian: cleared.isPrimaryGuardian,
    isTestingComparator: cleared.isTestingComparator,
    multipleBirth,
    phenotypicTrait: phenotypicTraitOut,
    clinicalNote: clinicalNoteOut,
  }
  if (secondaryConsent) {
    out.secondaryConsent = secondaryConsent
  }
  if (specimen) {
    out.specimen = specimen
  }
  if (kitShipmentRequested !== undefined) {
    // May be a value (request) or explicit null (clear a prior/orphaned kit).
    out.kitShipmentRequested = kitShipmentRequested
  }
  // R5: emit the phenotype list only when populated so callers without the
  // clinical block keep their existing payload shape (a cleared list omits the
  // key, which the upsert treats as "no phenotypes").
  if (cleared.hpoTerms.length > 0) {
    out.hpoTerms = cleared.hpoTerms
  }
  return out
}

/**
 * F.1b-iv-a: build a minimal valid `FamilyMember`-shaped object so we
 * can reuse `<ComparatorParentConsentSections>` (which is typed
 * against the legacy `FamilyMember` shape) without touching any
 * legacy component. The consent component reads only the three
 * consent fields below; the rest of `FamilyMember` / `info` is filled
 * with safe defaults that never reach the DOM.
 */
function draftToFamilyMemberConsentView(draft: DraftState): FamilyMember {
  return {
    testingRequested: draft.isTestingComparator,
    isPrimaryGuardian: draft.isPrimaryGuardian,
    incidentalFindingsSelection: incidentalCanonicalToSelection(draft.includeIncidentalFindings),
    newYorkSampleRetentionOptIn: draft.newYorkSampleRetentionOptIn ?? undefined,
    researchContactConsent: draft.researchContactConsent ?? undefined,
    info: {
      firstName: draft.firstName,
      lastName: draft.lastName,
      dateOfBirth: draft.dateOfBirth,
      medicalRecordNumber: draft.mrn,
      email: draft.email,
      phone: draft.phone,
      phenotypicTrait: PhenotypicTrait.Unknown,
      clinicalNotes: '',
      phenotypes: [],
    },
  }
}

/**
 * Small native-select wrapper styled to match `TextInput` (F.1a-local; promote
 * to `common/Modal.tsx` when more callers exist).
 */
interface SelectFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<{ value: string; label: string }>
  required?: boolean
  error?: string
  showError?: boolean
  placeholder?: string
  /**
   * Optional helper text rendered between the label and the select
   * (body-subtle), mirroring RadioOption's description style (UX-008).
   */
  description?: string
  /** When true, the select is read-only (used to lock the fixed MOTHER/FATHER relationship). */
  disabled?: boolean
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required,
  error,
  showError = false,
  placeholder,
  description,
  disabled = false,
}: SelectFieldProps) {
  const id = useId()
  const hasError = Boolean(showError && error)
  const errorId = hasError ? `${id}-error` : undefined
  const descriptionId = description ? `${id}-description` : undefined
  const markerState = resolveFieldMarkerState(Boolean(required), value !== '')
  return (
    <div className="flex items-stretch gap-2">
      <FieldRequiredRail state={markerState} />
      <div className="flex min-w-0 flex-1 flex-col">
      <div className="mb-1.5 flex items-center">
        <label
          htmlFor={id}
          className={`text-sm font-medium ${
            hasError ? 'text-error-600 dark:text-error-100' : 'text-text-heading dark:text-white'
          }`}
        >
          {label}
        </label>
        <FieldMarker state={markerState} fieldLabel={label} />
      </div>
      {description ? (
        <p id={descriptionId} className="mb-1.5 text-xs leading-5 text-text-body-subtle dark:text-gray-500">
          {description}
        </p>
      ) : null}
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={hasError}
          aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
          className={`w-full appearance-none rounded-base border pl-4 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 ${
            hasError
              ? 'border-error-500 bg-error-50 text-text-fg-error-strong focus:border-error-600 focus:ring-error-500 dark:bg-gray-900 dark:text-error-100'
              : 'border-border-base bg-white text-text-heading focus:border-brand-600 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:focus:border-brand-500'
          } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${hasError ? 'text-error-600 dark:text-error-100' : 'text-text-body dark:text-gray-400'}`}
          aria-hidden
        />
      </div>
      {hasError && error ? (
        <p id={errorId} className="mt-2.5 text-sm text-error-600 dark:text-error-100" role="alert">
          {error}
        </p>
      ) : null}
      </div>
    </div>
  )
}

const PHENOTYPE_TYPE_LABELS: Record<HPOTerm['type'], string> = {
  key: 'Key',
  additional: 'Additional',
  excluded: 'Pertinent Negative',
}

function ComparatorPhenotypeRow({
  term,
  onTypeChange,
  onRemove,
  readOnly = false,
}: {
  term: HPOTerm
  onTypeChange: (termId: string, type: HPOTerm['type']) => void
  onRemove: (termId: string) => void
  readOnly?: boolean
}) {
  return (
    <div className="flex w-full items-center gap-3 border-b border-border-base py-3 last:border-b-0 dark:border-gray-700">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-text-heading dark:text-white">
          {term.name}
        </span>
        {term.hpoCode && (
          <span className="text-xs text-text-body-subtle dark:text-gray-500">{term.hpoCode}</span>
        )}
      </div>
      <div className="flex shrink-0 items-center">
        {(['key', 'additional', 'excluded'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onTypeChange(term.id, type)}
            aria-pressed={term.type === type}
            disabled={readOnly}
            className={`border px-2.5 py-1 text-xs font-medium transition-colors first:rounded-l-base last:rounded-r-base ${
              term.type === type
                ? 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-800 dark:bg-gray-800 dark:text-brand-400'
                : 'border-border-base bg-white text-text-body hover:bg-background-secondary-medium dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800'
            }${readOnly ? ' cursor-not-allowed opacity-60' : ''}`}
          >
            {PHENOTYPE_TYPE_LABELS[type]}
          </button>
        ))}
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={() => onRemove(term.id)}
          className="shrink-0 p-1 text-text-body-subtle hover:text-text-body dark:text-gray-500 dark:hover:text-gray-400"
          aria-label={`Remove ${term.name}`}
        >
          <Close className="size-4" aria-hidden />
        </button>
      )}
    </div>
  )
}

export function ComparatorModal({
  isOpen,
  onClose,
  initialData,
  fieldGroups = 'full',
  onSave,
  continueTo,
  defaultRelationship,
  relationshipOptions = ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS,
  hidePrimaryGuardianToggle = false,
  enableTestingToggle = false,
  enableKitRequest = false,
  comparatorConsentConfig,
  enableClinicalFields = false,
  clinicalNoteBudget = RCIGM_COMPARATOR_CLINICAL_NOTE_BUDGET,
  suppressHpoEditing = false,
  enableSampleType = false,
  parentSampleTypes,
  labAddress,
  mrnRequired = false,
  lockSampleType = false,
  patientResearchContactOptIn = false,
  patientResearchContactAppliesToThisParent = false,
  probandResearchContactPrimaryGuardian = false,
  platformCollectsConsentToTest = false,
  guardianContactOnFile = false,
  onRequestGuardianContact,
  coveredByGuardianModal = false,
  probandIncidentalFindingsSelection = null,
  requireClinicalIdentity = false,
  autosave = false,
  legacyFixedParentDefaults = false,
  isEpicFlow = false,
  epicState,
  guardianPrefill,
}: ComparatorModalProps) {
  const [draft, setDraftRaw] = useState<DraftState>(() =>
    applyComparatorContactPolicy(buildInitialDraft(initialData ?? null, defaultRelationship, guardianPrefill))
  )
  const displayHpoTerms = useResolvedHpoTermNames(draft.hpoTerms)
  // Single write path: every draft commit re-applies contact policy so strip /
  // keep-on-pending-DOB cannot drift across open, prefill, and field updates.
  const setDraft = useCallback((updater: DraftState | ((prev: DraftState) => DraftState)) => {
    setDraftRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      return applyComparatorContactPolicy(next)
    })
  }, [])
  const [showErrors, setShowErrors] = useState(false)
  const [phenotypeSearchOpen, setPhenotypeSearchOpen] = useState(false)

  // Epic patient-demographics MRN lookup (POST /fhir/patient/search). State
  // only matters when `isEpicFlow` is on and the demographics group is shown;
  // otherwise the lookup UI + effect below are inert.
  const [mrnLookupLoading, setMrnLookupLoading] = useState(false)
  const [mrnLookupMessage, setMrnLookupMessage] = useState<string | null>(null)

  // Keep latest props on refs so the open-time hydration effect can read them
  // without depending on their identity. With autosave on, `onSave` dispatches
  // `upsertComparator` on every edit, which changes `initialData` identity — if
  // the effect re-ran on that, it would reset the draft mid-typing (the same
  // race the other autosaving section modals avoid).
  const initialDataRef = useRef(initialData)
  initialDataRef.current = initialData
  const defaultRelationshipRef = useRef(defaultRelationship)
  defaultRelationshipRef.current = defaultRelationship
  // Latest guardian pre-fill on a ref so the open-time hydration effect reads
  // the current value without re-running when its identity changes (parent
  // objects are frequently re-created); the effect keys on `isOpen` only.
  const guardianPrefillRef = useRef(guardianPrefill)
  guardianPrefillRef.current = guardianPrefill
  // UX-056: tracks the sex value we last auto-seeded from the relationship so a
  // relationship change can re-seed only while the user hasn't manually touched
  // the field. `null` once the user picks their own value (or there's no
  // implied default). Initialized from the open-time draft: if that draft's sex
  // equals the relationship's implied default, treat it as auto-seeded.
  const autoSeededSexRef = useRef<Sex | null>(
    draft.sex !== Sex.Unknown && draft.sex === defaultSexForRelationship(draft.relationship)
      ? draft.sex
      : null
  )
  // Open-time baseline so Discard can revert autosave-mutated canonical.
  const openBaselineRef = useRef<Comparator | null>(initialData ?? null)

  // Latest draft/onSave on refs so the demographics-lookup runner can read the
  // current values (and merge results without clobbering in-flight edits)
  // without being re-created on every keystroke.
  const draftRef = useRef(draft)
  draftRef.current = draft
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave
  // Persist function used by the async demographics lookup to commit an
  // auto-filled MRN immediately. In autosave mode the lookup can resolve while
  // the visible group has an unrelated error (e.g. sex not yet set), which
  // suppresses `useAutosave`; and even when valid, `useAutosave`'s
  // disabled-window baseline advance can swallow the seeded value before it is
  // diffed. Committing through this ref guarantees the matched MRN reaches
  // canonical. Assigned below once `draftToComparator`/`withConsentDefaults`
  // exist; the lookup runner (empty-dep callback) reads it via the ref.
  const commitSeededDraftRef = useRef<((next: DraftState) => void) | null>(null)
  const epicStateRef = useRef(epicState)
  epicStateRef.current = epicState
  // Debounce timer, in-flight abort controller, and a monotonically-increasing
  // sequence so a stale (superseded) lookup can never apply its result.
  const mrnLookupDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mrnLookupAbortRef = useRef<AbortController | null>(null)
  const mrnLookupSeqRef = useRef(0)

  useEffect(() => {
    if (!isOpen) return
    openBaselineRef.current = initialDataRef.current ?? null
    // setDraft re-applies contact policy on every commit — no outer apply here.
    const nextDraft = buildInitialDraft(
      initialDataRef.current ?? null,
      defaultRelationshipRef.current,
      guardianPrefillRef.current
    )
    setDraft(nextDraft)
    // Re-baseline the auto-seed tracker for the freshly-opened comparator.
    autoSeededSexRef.current =
      nextDraft.sex !== Sex.Unknown && nextDraft.sex === defaultSexForRelationship(nextDraft.relationship)
        ? nextDraft.sex
        : null
    setShowErrors(false)
    setPhenotypeSearchOpen(false)
    // Reset the demographics-lookup UI and cancel any in-flight/debounced query
    // so a freshly-opened modal never shows a stale message or applies a result
    // meant for a previously-edited comparator.
    setMrnLookupMessage(null)
    setMrnLookupLoading(false)
    if (mrnLookupDebounceRef.current) {
      clearTimeout(mrnLookupDebounceRef.current)
      mrnLookupDebounceRef.current = null
    }
    if (mrnLookupAbortRef.current) {
      mrnLookupAbortRef.current.abort()
      mrnLookupAbortRef.current = null
    }
    mrnLookupSeqRef.current += 1
  }, [isOpen, setDraft])

  const errors = useMemo(
    () =>
      computeErrors(draft, {
        clinicalNoteBudget,
        enableSampleType,
        mrnRequired,
        lockSampleType,
        patientResearchContactOptIn,
        patientResearchContactAppliesToThisParent,
        probandResearchContactPrimaryGuardian,
        platformCollectsConsentToTest,
        guardianContactOnFile,
        requireClinicalIdentity,
      }),
    [
      draft,
      clinicalNoteBudget,
      enableSampleType,
      mrnRequired,
      lockSampleType,
      patientResearchContactOptIn,
      patientResearchContactAppliesToThisParent,
      probandResearchContactPrimaryGuardian,
      platformCollectsConsentToTest,
      guardianContactOnFile,
      requireClinicalIdentity,
    ]
  )
  // R5: only errors for fields the active group renders can block save.
  const visibleErrors = useMemo(() => {
    const allowed = ERROR_KEYS_BY_GROUP[fieldGroups]
    const out: FieldErrors = {}
    for (const key of Object.keys(errors) as Array<keyof FieldErrors>) {
      if (allowed.includes(key)) out[key] = errors[key]
    }
    return out
  }, [errors, fieldGroups])

  // Consent gate: a `requireInteraction` section that is applicable but still
  // unanswered blocks save the same way a missing required field does. The
  // consent field group contributes no keys to `computeErrors` (it has no plain
  // inputs), so this synthetic `consent` error is what lets a Done/Continue on
  // an unanswered required consent actually block — flipping `showErrors` so the
  // inline row error surfaces. GEN-3484: a proband opt-out locks the comparator
  // incidental to opt-out (resolved as an explicit answer), so it never counts
  // as unanswered here.
  //
  // Gated on the SAME visibility predicate as the rendered consent block
  // (`showConsentBlock`, replicated here since it is declared later): in the
  // combined `full` modal the consent block only renders for a testing parent,
  // so a sibling / non-testing / non-parent comparator must NOT be blocked by an
  // unanswered consent it can neither see nor change.
  const consentInteractionError = useMemo(() => {
    if (!comparatorConsentConfig) return undefined
    const consentGroupShown = fieldGroups === 'full' || fieldGroups === 'consent'
    const consentBlockVisible =
      consentGroupShown &&
      comparatorConsentAnyApplicable(comparatorConsentConfig) &&
      (fieldGroups === 'consent' ||
        (draft.isTestingComparator &&
          parentSideForComparator({
            relationship: draft.relationship as FamilyMemberRelationship,
          }) != null))
    if (!consentBlockVisible) return undefined
    const gates = resolveConsentInteractionGates(comparatorConsentConfig)
    const incidentalLockedToOptOut = probandIncidentalFindingsLocksComparator(probandIncidentalFindingsSelection)
    const incidentalUnanswered =
      gates.incidentalRequireInteraction && !incidentalLockedToOptOut && draft.includeIncidentalFindings == null
    const nyUnanswered = gates.nyRetentionRequireInteraction && draft.newYorkSampleRetentionOptIn == null
    const researchUnanswered = gates.researchContactRequireInteraction && draft.researchContactConsent == null
    return incidentalUnanswered || nyUnanswered || researchUnanswered
      ? CONSENT_REQUIRED_INTERACTION_ERROR
      : undefined
  }, [
    comparatorConsentConfig,
    fieldGroups,
    draft.isTestingComparator,
    draft.relationship,
    probandIncidentalFindingsSelection,
    draft.includeIncidentalFindings,
    draft.newYorkSampleRetentionOptIn,
    draft.researchContactConsent,
  ])

  const blockingErrors = useMemo(
    () => (consentInteractionError ? { ...visibleErrors, consent: consentInteractionError } : visibleErrors),
    [visibleErrors, consentInteractionError]
  )
  // Autosave (R5 section editor) is gated on `hasErrors`; keep it scoped to the
  // plain field errors so a partially-answered consent block can still persist
  // each choice as the provider makes it (Done is gated separately via
  // `saveBlocked`). Folding the consent gate in here would stall autosave until
  // every required section is answered at once.
  const hasErrors = Object.keys(visibleErrors).length > 0
  // Explicit Save / Continue gate: the plain field errors PLUS an unanswered
  // `requireInteraction` consent. A blocked Done flips `showErrors`, surfacing
  // the inline row error the reviewer flagged as never appearing.
  const saveBlocked = Object.keys(blockingErrors).length > 0

  // Incidental Findings requires an explicit provider choice (the section is
  // authored `requireInteraction: true` and renders Opt-In / Opt-Out radios),
  // so an untouched control stays `null` (unanswered) and the submit gate
  // blocks on it. We no longer coerce `null → false` on save; `null` is the
  // genuine "unanswered" sentinel. Kept as an identity passthrough so the save
  // call sites don't need to change.
  const withConsentDefaults = useCallback((d: DraftState): DraftState => d, [])

  // Debounce-autosave the draft while the visible group is valid. Gating the
  // hook on `!hasErrors` keeps invalid drafts out of canonical and prevents a
  // misleading "Saved" indicator. Disabled entirely unless the caller opts in.
  const autosaveOnSave = useCallback(
    (next: DraftState) => {
      onSave(draftToComparator(withConsentDefaults(next)))
    },
    [onSave, withConsentDefaults]
  )
  // Commit an auto-filled MRN straight to canonical in autosave mode (see the
  // ref declaration above for why the normal `useAutosave` diff isn't enough).
  // The draft is initialized from the stored comparator, so committing it here
  // never clobbers previously-saved fields with stale nulls.
  commitSeededDraftRef.current = autosave
    ? (next: DraftState) => onSaveRef.current(draftToComparator(withConsentDefaults(next)))
    : null
  const { lastSaveTime, isSaving } = useAutosave(
    draft,
    autosaveOnSave,
    500,
    autosave && isOpen && !hasErrors
  )

  // R5: per-group block visibility. `full` preserves the legacy surface.
  const showRelationship =
    fieldGroups === 'full' || fieldGroups === 'create' || fieldGroups === 'demographics'
  const showIdentity = fieldGroups === 'full' || fieldGroups === 'demographics'
  const showPrimaryGuardian =
    !hidePrimaryGuardianToggle &&
    (fieldGroups === 'full' || fieldGroups === 'create' || fieldGroups === 'demographics')
  const showTestingToggle =
    enableTestingToggle && (fieldGroups === 'full' || fieldGroups === 'demographics')
  const showMultipleBirthBlock = fieldGroups === 'full' || fieldGroups === 'demographics'
  const showClinicalBlock =
    enableClinicalFields && (fieldGroups === 'full' || fieldGroups === 'clinical')
  const showSampleTypeBlock =
    enableSampleType &&
    (fieldGroups === 'full' || fieldGroups === 'create' || fieldGroups === 'demographics')
  const showKitRequestBlock =
    enableKitRequest &&
    draft.isTestingComparator &&
    (fieldGroups === 'full' || fieldGroups === 'demographics' || fieldGroups === 'create')
  const showConsentGroup = fieldGroups === 'full' || fieldGroups === 'consent'
  // GEN-4734: a home-shipped kit requires its address fields (Apt/suite excepted).
  const kitAddressRequired = draft.requestKit && draft.kitLocation === 'HOME'
  const isMultiple = isMultipleBirthRelationship(draft.relationship)

  // The picker options are restricted to the relationships the provider-api
  // accepts (see `API_SUPPORTED_RELATIONSHIP_VALUES`). When editing a comparator
  // whose stored relationship predates that restriction (e.g. TWIN_SIBLING,
  // LEGAL_GUARDIAN, an extended lineage value), append it as an extra option so
  // the control reflects the real selection instead of silently rendering blank
  // — which would let the user unknowingly overwrite it on save.
  const relationshipOptionsWithCurrent = useMemo(() => {
    const current = draft.relationship?.trim()
    if (!current || relationshipOptions.some((o) => o.value === current)) {
      return relationshipOptions
    }
    return [
      ...relationshipOptions,
      { value: current as (typeof relationshipOptions)[number]['value'], label: formatComparatorRelationshipLabel(current) },
    ]
  }, [relationshipOptions, draft.relationship])

  // Lock the relationship for the fixed biological-parent rows in the legacy
  // (non-side-nav) flow only. MOTHER / FATHER have dedicated cards and must
  // never be re-typed to another relationship, so when editing an existing
  // parent via the legacy edit-pencil path the relationship select is
  // read-only. The R5 side-nav editors and add / create flows pass
  // `legacyFixedParentDefaults = false` and keep it editable.
  const lockRelationship =
    legacyFixedParentDefaults &&
    (initialData?.relationship === 'MOTHER' || initialData?.relationship === 'FATHER')

  // The primary-guardian obligation gets an explanatory help line (the proband
  // opted into research contact, so their guardian owns the contact channel).
  const primaryGuardianContactRequired =
    probandResearchContactPrimaryGuardian === true && draft.isPrimaryGuardian === true
  // The comparator opted into research contact for THEMSELVES (their own toggle).
  // Like the primary-guardian obligation, this gets an always-on explanatory line
  // rather than waiting for a save attempt.
  const ownResearchContactRequired = draft.isTestingComparator && draft.researchContactConsent === true
  // An obligation whose explanatory line shows up-front (before any save attempt),
  // mirroring the always-on nature of these consents. The line still clears once
  // a channel is provided (it acts as a prompt, not a permanent banner).
  const platformContactRequired = platformCollectsConsentToTest && draft.isTestingComparator
  const contactChannelObligationExplained =
    primaryGuardianContactRequired || ownResearchContactRequired || platformContactRequired
  const guardianContactNeeded = minorNeedsGuardianContact(draft, {
    platformCollectsConsentToTest,
    guardianContactOnFile,
  })
  // Same args as `computeErrors` → `requiredComparatorFields` so Email/Phone
  // labels and missing-state cannot diverge from save validation.
  const requiredFields = useMemo(
    () =>
      requiredComparatorFields({
        isTesting: draft.isTestingComparator,
        enableSampleType,
        lockSampleType,
        mrnRequired,
        researchContactRequired:
          draft.researchContactConsent === true ||
          (patientResearchContactOptIn === true && patientResearchContactAppliesToThisParent === true),
        primaryGuardianResearchContactRequired: primaryGuardianContactRequired,
        platformCollectsConsentToTest,
        requiresClinicalIdentity: requireClinicalIdentity && draft.isTestingComparator,
        dateOfBirth: draft.dateOfBirth,
      }),
    [
      draft.isTestingComparator,
      draft.researchContactConsent,
      draft.dateOfBirth,
      enableSampleType,
      lockSampleType,
      mrnRequired,
      patientResearchContactOptIn,
      patientResearchContactAppliesToThisParent,
      primaryGuardianContactRequired,
      platformCollectsConsentToTest,
      requireClinicalIdentity,
    ]
  )
  const contactChannelEnforced = requiredFields.has('contactChannel')
  // Mark both Email and Phone as incomplete (red border/label; the shared
  // "email OR phone" line explains why) when a contact channel is required but
  // neither is filled. The always-explained obligations surface immediately; the
  // projected patient opt-in only surfaces after a save attempt (`showErrors`).
  const contactPolicy = comparatorContactPolicy(draft.dateOfBirth)
  const contactsDisabled = contactFieldsDisabled(contactPolicy)
  const showContactHelper = contactHelperVisible(contactPolicy)
  const contactHelperId = useId()
  const contactChannelMissing =
    contactChannelEnforced && !draft.email.trim() && !draft.phone.trim()
  const contactChannelInvalid =
    contactChannelMissing && (contactChannelObligationExplained || showErrors)

  // F.1b-iv-c: visible allowlist for the sample-type radio row.
  // `parentSampleTypes` is treated as a read-only allowlist provided
  // by the caller; the default omits saliva (not offered for parents).
  //
  // Dedup defensively (first-seen order): the allowlist is assembled
  // independently at several call sites from remote `parentSampleTypes`
  // config, none of which dedup. A config that repeats a value (e.g. two
  // `buccal` entries) would otherwise render duplicate radios with colliding
  // React keys — the source of the inconsistent Saliva/Buccal behavior. This
  // mirrors the proband path (`TestOptionsModal`), which already dedups.
  const availableSampleTypes = useMemo<ReadonlyArray<SampleTypeOption>>(() => {
    const source =
      parentSampleTypes && parentSampleTypes.length > 0 ? parentSampleTypes : DEFAULT_PARENT_SAMPLE_TYPES
    const seen = new Set<SampleTypeOption>()
    const unique: SampleTypeOption[] = []
    for (const sampleType of source) {
      if (!seen.has(sampleType)) {
        seen.add(sampleType)
        unique.push(sampleType)
      }
    }
    return unique
  }, [parentSampleTypes])

  // F.1b-iv-c: if the allowlist changes and the currently-selected
  // sample type is no longer permitted, clear the selection.
  // F.1b-vii-a: skip auto-correction under lock — the value is
  // caller-supplied, so the SPA must not silently null it.
  useEffect(() => {
    if (!enableSampleType || lockSampleType) return
    if (draft.sampleType !== null && !availableSampleTypes.includes(draft.sampleType)) {
      setDraft((prev) => ({ ...prev, sampleType: null }))
    }
  }, [enableSampleType, lockSampleType, availableSampleTypes, draft.sampleType, setDraft])

  // Render the secondary-consent block when the caller supplies an applicable
  // config. R5 (focused `consent` editor) decouples consent from relationship:
  // every comparator — biological parent OR "other comp" — has a Consent
  // section (matches `<ComparatorDetailView>`), so the parent-only / testing
  // gates apply ONLY to the combined `full` modal (legacy parent-pencil flow,
  // F.1b-iv-a) where consent is still scoped to MOTHER / FATHER participants.
  const showConsentBlock =
    showConsentGroup &&
    comparatorConsentConfig != null &&
    comparatorConsentAnyApplicable(comparatorConsentConfig) &&
    (fieldGroups === 'consent' ||
      (draft.isTestingComparator &&
        parentSideForComparator({
          relationship: draft.relationship as FamilyMemberRelationship,
        }) != null))

  const update = useCallback(<K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }, [setDraft])

  // Whether the demographics-lookup surface is relevant: only in an Epic launch,
  // and only where the demographics identity fields are actually rendered.
  const showPatientLookup = isEpicFlow && showIdentity
  const patientLookupAvailable = showPatientLookup && isEpicPatientDemographicsLookupAvailable(epicState)
  const canRunPatientDemographicsLookup = Boolean(
    draft.firstName.trim() && draft.lastName.trim() && draft.dateOfBirth.trim()
  )

  /**
   * Query `POST /fhir/patient/search` for the comparator's demographics and
   * reconcile the result into the MRN field:
   *   - exactly one match with an MRN → fill MRN (auto only when MRN empty),
   *   - one match but no MRN → prompt to enter it manually,
   *   - more than one match → tell the user to enter MRN manually,
   *   - no match → tell the user nothing matched.
   * `mode` distinguishes the debounced background lookup (`auto`) from the
   * explicit search-button press (`manual`, always surfaces a message and will
   * overwrite an existing MRN). Email/phone are never auto-filled (providers
   * enter contact fields manually), matching the legacy parent modal.
   */
  const runPatientDemographicsLookup = useCallback(
    async (mode: 'auto' | 'manual', signal: AbortSignal) => {
      const latest = draftRef.current
      const first = latest.firstName.trim()
      const last = latest.lastName.trim()
      const dob = latest.dateOfBirth.trim()
      if (!first || !last || !dob) return

      const seq = ++mrnLookupSeqRef.current
      const mrnSnapshot = latest.mrn.trim()

      setMrnLookupLoading(true)
      if (mode === 'manual') setMrnLookupMessage(null)

      try {
        const result = await lookupPatientByDemographics(
          epicStateRef.current,
          { firstName: first, lastName: last, dateOfBirth: dob },
          signal
        )
        if (signal.aborted || mrnLookupSeqRef.current !== seq) return

        switch (result.status) {
          case 'not_configured':
            if (mode === 'manual') {
              setMrnLookupMessage(
                'Patient record lookup requires an Epic launch with an active session. Open the order from your EHR or restore your Epic connection.'
              )
            }
            break
          case 'found': {
            // Fill from the match into the POST-await draft so in-flight edits
            // are not clobbered by `latest` (captured pre-await). Auto mode only
            // fills an empty MRN; manual mode overwrites with the matched value.
            const applyMrn = Boolean(result.mrn.trim() && (mode === 'manual' || !mrnSnapshot))
            if (applyMrn) {
              const nextMrn = result.mrn.trim()
              let committedNext: DraftState | null = null
              setDraft((prev) => {
                const next = { ...prev, mrn: nextMrn }
                draftRef.current = next
                committedNext = next
                return next
              })
              // Commit the seeded MRN immediately in autosave mode. Relying on
              // the `useAutosave` diff loses it when the fill lands during a
              // disabled window (unrelated invalid field) — the baseline advance
              // then swallows the change so it is never persisted.
              if (committedNext) commitSeededDraftRef.current?.(committedNext)
            }
            if (mode === 'manual' || applyMrn) {
              setMrnLookupMessage(
                applyMrn
                  ? 'Medical record number was filled from a matched patient record.'
                  : 'A patient record matched; verify the medical record number.'
              )
            } else {
              setMrnLookupMessage(null)
            }
            break
          }
          case 'chart_found_mrn_missing':
            setMrnLookupMessage(
              'Patient chart was found, but an MRN was not found. Enter the medical record number manually if needed.'
            )
            break
          case 'ambiguous':
            setMrnLookupMessage(
              'More than one patient matched these demographics. Enter the medical record number manually.'
            )
            break
          case 'not_found':
            if (mode === 'manual' || !mrnSnapshot) {
              setMrnLookupMessage('No patient matched the specified criteria.')
            } else {
              setMrnLookupMessage(null)
            }
            break
          case 'error':
            setMrnLookupMessage(result.message)
            break
        }
      } catch {
        if (signal.aborted || mrnLookupSeqRef.current !== seq) return
        setMrnLookupMessage('Patient lookup failed. Try again later.')
      } finally {
        if (mrnLookupSeqRef.current === seq) setMrnLookupLoading(false)
      }
    },
    [setDraft]
  )

  // Debounced background lookup: fires 500ms after first/last/DOB settle for a
  // testing comparator in an Epic launch with a live session. Gated so it never
  // runs on standalone web, non-testing comparators, or non-demographics groups.
  useEffect(() => {
    if (!isOpen || !patientLookupAvailable || !draft.isTestingComparator) return

    const first = draft.firstName.trim()
    const last = draft.lastName.trim()
    const dob = draft.dateOfBirth.trim()
    if (!first || !last || !dob) {
      if (mrnLookupDebounceRef.current) {
        clearTimeout(mrnLookupDebounceRef.current)
        mrnLookupDebounceRef.current = null
      }
      if (mrnLookupAbortRef.current) {
        mrnLookupAbortRef.current.abort()
        mrnLookupAbortRef.current = null
      }
      mrnLookupSeqRef.current += 1
      setMrnLookupLoading(false)
      setMrnLookupMessage(null)
      return
    }

    if (mrnLookupDebounceRef.current) clearTimeout(mrnLookupDebounceRef.current)
    mrnLookupDebounceRef.current = setTimeout(() => {
      mrnLookupDebounceRef.current = null
      if (mrnLookupAbortRef.current) mrnLookupAbortRef.current.abort()
      const controller = new AbortController()
      mrnLookupAbortRef.current = controller
      void runPatientDemographicsLookup('auto', controller.signal)
    }, 500)

    return () => {
      // Cancel BOTH the pending debounce timer and any in-flight request, and
      // bump the sequence, when demographics (or gating) change or the modal
      // closes. Clearing only the timer left a race: if the debounce had already
      // fired, its request could still be in flight for the OLD first/last/DOB
      // and — since it was neither aborted nor superseded by a sequence bump —
      // resolve during the next debounce window and apply a stale MRN to the
      // now-edited draft. Aborting trips the `signal.aborted` guard and the
      // sequence bump trips the `mrnLookupSeqRef.current !== seq` guard, so any
      // late resolution for outdated demographics is discarded.
      if (mrnLookupDebounceRef.current) {
        clearTimeout(mrnLookupDebounceRef.current)
        mrnLookupDebounceRef.current = null
      }
      if (mrnLookupAbortRef.current) {
        mrnLookupAbortRef.current.abort()
        mrnLookupAbortRef.current = null
      }
      mrnLookupSeqRef.current += 1
      // Clear the spinner here too: a superseded/aborted request skips its own
      // `finally` loading-reset (that reset is gated on the sequence still
      // matching), so without this the search UI could stay in the loading
      // state until some later lookup completes or the modal closes. Safe
      // because cleanup runs before the next effect body schedules its debounce
      // — no newer request is in flight yet. A newly-debounced lookup re-sets
      // loading when it actually fires.
      setMrnLookupLoading(false)
    }
  }, [
    isOpen,
    patientLookupAvailable,
    draft.isTestingComparator,
    draft.firstName,
    draft.lastName,
    draft.dateOfBirth,
    runPatientDemographicsLookup,
  ])

  // Abort any in-flight lookup on unmount so a late resolution can't touch state.
  useEffect(
    () => () => {
      if (mrnLookupDebounceRef.current) clearTimeout(mrnLookupDebounceRef.current)
      if (mrnLookupAbortRef.current) mrnLookupAbortRef.current.abort()
    },
    []
  )

  const handleManualPatientLookup = useCallback(() => {
    if (mrnLookupDebounceRef.current) {
      clearTimeout(mrnLookupDebounceRef.current)
      mrnLookupDebounceRef.current = null
    }
    if (mrnLookupAbortRef.current) mrnLookupAbortRef.current.abort()
    const controller = new AbortController()
    mrnLookupAbortRef.current = controller
    void runPatientDemographicsLookup('manual', controller.signal)
  }, [runPatientDemographicsLookup])

  const patientLookupButtonTitle = mrnLookupLoading
    ? 'Searching…'
    : !canRunPatientDemographicsLookup
      ? 'Enter first name, last name, and date of birth to search.'
      : !patientLookupAvailable
        ? 'Epic session required.'
        : undefined

  // Fill the kit shipping-address fields from a resolved autosuggest selection.
  // Only overwrite a field when the resolver returned a value (partial results
  // must not blank out anything the user already typed).
  const applyResolvedKitAddress = useCallback(
    (addr: {
      street?: string | null
      city?: string | null
      state?: string | null
      zip?: string | null
    }) => {
      setDraft((prev) => ({
        ...prev,
        kitStreet: addr.street ?? prev.kitStreet,
        kitCity: addr.city ?? prev.kitCity,
        kitState: addr.state ?? prev.kitState,
        kitZip: addr.zip ?? prev.kitZip,
      }))
    },
    [setDraft]
  )

  // GEN-4933: the kit destination allowlist. A Whole Blood sample ships only to
  // the clinic / site (blood is drawn on-site), so "Patient home" is dropped;
  // every other sample type keeps the full list.
  const kitLocationOptions = useMemo(
    () => (draft.sampleType === 'whole-blood' ? WHOLE_BLOOD_KIT_LOCATION_OPTIONS : KIT_LOCATION_OPTIONS),
    [draft.sampleType]
  )

  // GEN-4933: a Clinic / site kit ships to the organization's own address, so
  // pre-fill the shipment address from `labAddress` (org config). Switching to
  // Home clears every address field — a patient home is never the clinic
  // address, so the pre-filled org address must not linger for the user to
  // submit by mistake. Blank fields when no org address is configured, too.
  const applyKitLocationToDraft = useCallback(
    (prev: DraftState, location: string): DraftState => {
      if (location === 'CLINIC') {
        return {
          ...prev,
          kitLocation: location,
          kitStreet: labAddress?.street ?? '',
          kitStreet2: labAddress?.street2 ?? '',
          kitCity: labAddress?.city ?? '',
          kitState: labAddress?.state ?? '',
          kitZip: labAddress?.zip ?? '',
        }
      }
      if (location === 'HOME') {
        return {
          ...prev,
          kitLocation: location,
          kitStreet: '',
          kitStreet2: '',
          kitCity: '',
          kitState: '',
          kitZip: '',
        }
      }
      return { ...prev, kitLocation: location }
    },
    [labAddress]
  )

  const handleKitLocationChange = useCallback(
    (location: string) => {
      setDraft((prev) => applyKitLocationToDraft(prev, location))
    },
    [applyKitLocationToDraft, setDraft]
  )

  // GEN-4933: when Whole Blood is the sample type, the only valid destination is
  // Clinic / site. Force it (and pre-fill the org address) whenever a kit is
  // requested but the destination is anything else — e.g. after switching the
  // sample type from Buccal (home) to Whole Blood.
  useEffect(() => {
    if (!enableKitRequest) return
    if (draft.sampleType === 'whole-blood' && draft.requestKit && draft.kitLocation !== 'CLINIC') {
      setDraft((prev) => applyKitLocationToDraft(prev, 'CLINIC'))
    }
  }, [enableKitRequest, draft.sampleType, draft.requestKit, draft.kitLocation, applyKitLocationToDraft, setDraft])

  const addPhenotype = useCallback((suggestion: HPOSuggestion) => {
    setDraft((prev) => {
      if (prev.hpoTerms.some((t) => t.id === suggestion.id)) return prev
      const term: HPOTerm = {
        id: suggestion.id,
        name: suggestion.name,
        hpoCode: suggestion.hpoCode,
        type: suggestion.type ?? 'key',
      }
      return { ...prev, hpoTerms: [...prev.hpoTerms, term] }
    })
  }, [setDraft])

  const removePhenotype = useCallback((termId: string) => {
    setDraft((prev) => ({ ...prev, hpoTerms: prev.hpoTerms.filter((t) => t.id !== termId) }))
  }, [setDraft])

  const changePhenotypeType = useCallback((termId: string, type: HPOTerm['type']) => {
    setDraft((prev) => ({
      ...prev,
      hpoTerms: prev.hpoTerms.map((t) => (t.id === termId ? { ...t, type } : t)),
    }))
  }, [setDraft])

  const updateMultipleBirth = useCallback(
    <K extends keyof ComparatorMultipleBirth>(key: K, value: ComparatorMultipleBirth[K]) => {
      setDraft((prev) => {
        const next: ComparatorMultipleBirth = { ...(prev.multipleBirth ?? emptyMultipleBirth()) }
        next[key] = value
        return { ...prev, multipleBirth: next }
      })
    },
    [setDraft]
  )

  useEffect(() => {
    if (!isMultiple && draft.multipleBirth) {
      setDraft((prev) => ({ ...prev, multipleBirth: null }))
    } else if (isMultiple && !draft.multipleBirth) {
      setDraft((prev) => ({ ...prev, multipleBirth: emptyMultipleBirth() }))
    }
  }, [isMultiple, draft.multipleBirth, setDraft])

  // GEN-2998: banner ref so a blocked save can scroll the error summary
  // into view at the top of the (scrollable) modal body.
  const errorBannerRef = useRef<HTMLDivElement | null>(null)

  const focusSaveBlockedBanner = useCallback(() => {
    // Defer until the banner has been rendered for this click; move focus so
    // keyboard / SR users land on the error summary (not only scroll).
    requestAnimationFrame(() => {
      const banner = errorBannerRef.current
      if (!banner) return
      banner.scrollIntoView({ block: 'start', behavior: 'smooth' })
      banner.focus()
    })
  }, [])

  const handleSave = useCallback(() => {
    if (saveBlocked) {
      setShowErrors(true)
      focusSaveBlockedBanner()
      if (guardianContactNeeded) onRequestGuardianContact?.()
      return
    }
    onSave(draftToComparator(withConsentDefaults(draft)))
    onClose()
  }, [
    saveBlocked,
    guardianContactNeeded,
    onRequestGuardianContact,
    onSave,
    onClose,
    draft,
    withConsentDefaults,
    focusSaveBlockedBanner,
  ])

  const handleContinue = useCallback(() => {
    if (!continueTo) return
    if (saveBlocked) {
      setShowErrors(true)
      focusSaveBlockedBanner()
      if (guardianContactNeeded) onRequestGuardianContact?.()
      return
    }
    onSave(draftToComparator(withConsentDefaults(draft)))
    continueTo.onContinue()
  }, [continueTo, saveBlocked, guardianContactNeeded, onRequestGuardianContact, onSave, draft, withConsentDefaults, focusSaveBlockedBanner])

  const handleDiscard = useCallback(() => {
    // When autosaving, edits have already landed in canonical, so revert to the
    // open-time baseline before closing; otherwise close is a plain cancel.
    if (autosave && openBaselineRef.current) {
      onSave(openBaselineRef.current)
    }
    onClose()
  }, [autosave, onSave, onClose])

  const title =
    fieldGroups === 'create'
      ? 'Add Comparator'
      : fieldGroups === 'demographics'
        ? 'Edit demographics'
        : fieldGroups === 'clinical'
          ? 'Edit clinical history'
          : fieldGroups === 'consent'
            ? 'Edit additional options'
            : initialData
              ? 'Edit family member'
              : 'Add family member'

  // GEN-2998: ordered list of failing-field labels for the save-blocked banner.
  const blockedFieldLabels = useMemo(
    () =>
      (Object.keys(blockingErrors) as Array<keyof FieldErrors>)
        .map((k) => FIELD_ERROR_LABELS[k])
        .filter((label): label is string => typeof label === 'string'),
    [blockingErrors],
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      hidden={phenotypeSearchOpen || coveredByGuardianModal}
      footer={
        <ModalFooter
          onDiscard={autosave ? handleDiscard : onClose}
          onClose={continueTo ? handleContinue : handleSave}
          discardLabel={autosave ? 'Discard Changes' : 'Cancel'}
          closeLabel={continueTo ? continueTo.label : 'Add to Order'}
          closeIcon={continueTo ? <ArrowRight className="h-4 w-4 shrink-0" aria-hidden /> : undefined}
          lastSaveTime={autosave ? lastSaveTime : null}
          isSaving={autosave ? isSaving : false}
        />
      }
    >
      <div className="flex flex-col gap-5">
        {showErrors && saveBlocked ? (
          <div
            ref={errorBannerRef}
            role="alert"
            aria-live="polite"
            tabIndex={-1}
            data-testid="comparator-modal-save-blocked-banner"
            className="rounded-base border border-border-error-subtle bg-error-50 p-3 text-sm text-error-700 outline-none focus-visible:ring-2 focus-visible:ring-error-500 dark:border-error-700 dark:bg-gray-900 dark:text-error-100"
          >
            <p className="font-medium">Please fix the following before saving:</p>
            <ul className="mt-1 list-inside list-disc">
              {blockedFieldLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {showRelationship && (
          <SelectField
            label="Biological Relative"
            value={draft.relationship}
            onChange={(v) => {
              const next = v as FamilyMemberRelationship
              // Guardian → comparator pre-fill on selection: when the chosen
              // relationship matches the guardian's unique relationship, fill
              // the EMPTY identity/contact fields from the guardian in the same
              // update that sets the relationship (empty-only — a value the user
              // already typed is never overwritten). Mirrors the open-time
              // `buildInitialDraft` pre-fill so both entry points behave alike.
              const doGuardianPrefill = guardianMatchesRelationship(guardianPrefillRef.current, next)
              setDraft((prev) => {
                if (!doGuardianPrefill) return { ...prev, relationship: next }
                const g = guardianPrefillRef.current
                const fillEmpty = (current: string, guardianValue: string | null | undefined): string =>
                  current.trim() !== '' ? current : strOrEmpty(guardianValue)
                return {
                  ...prev,
                  relationship: next,
                  firstName: fillEmpty(prev.firstName, g?.firstName),
                  lastName: fillEmpty(prev.lastName, g?.lastName),
                  dateOfBirth: fillEmpty(prev.dateOfBirth, g?.dateOfBirth),
                  email: fillEmpty(prev.email, g?.email),
                  phone: fillEmpty(prev.phone, g?.phone),
                }
              })
              // UX-056: seed "sex assigned at birth" from the newly chosen
              // relationship whenever it implies one — in every flow (the
              // add-family-member select isn't locked, so this fires there and
              // fixes the blank-sex bug). Only seed while the sex field is
              // "untouched": either unset, or still equal to the value we last
              // auto-seeded (re-seed on relationship change). A manual pick
              // clears `autoSeededSexRef`, so we never clobber a user's choice.
              const seededSex = defaultSexForRelationship(next)
              const sexIsUntouched =
                draft.sex === Sex.Unknown || draft.sex === autoSeededSexRef.current
              if (sexIsUntouched) {
                if (seededSex) {
                  update('sex', seededSex)
                  autoSeededSexRef.current = seededSex
                } else if (draft.sex === autoSeededSexRef.current) {
                  // New relationship implies no sex; clear the prior auto-seed
                  // so the field reverts to blank rather than keeping a stale
                  // gendered default.
                  update('sex', Sex.Unknown)
                  autoSeededSexRef.current = null
                }
              }
            }}
            options={relationshipOptionsWithCurrent}
            required
            disabled={lockRelationship}
            error={errors.relationship}
            showError={showErrors}
            placeholder="Select a biological relative…"
          />
        )}

        {showPrimaryGuardian && (
          // UX: the guardian toggle governs the contact fields below, so it sits
          // directly under the Relationship field (or, when Relationship is
          // hidden in a demographics-only editor, at the top of the identity
          // block). A contact channel is required only when a research-contact
          // opt-in applies (the comparator's own or the proband's), not merely
          // because this row is the primary guardian.
          <Toggle
            label="Primary guardian"
            description="Use this contact for consent and communication for the proband."
            checked={draft.isPrimaryGuardian}
            onChange={(v) => update('isPrimaryGuardian', v)}
            size="panel"
          />
        )}

        {showIdentity && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                label="First name"
                value={draft.firstName}
                onChange={(v) => update('firstName', v)}
                required
                maxLength={NAME_MAX_LENGTH}
                error={errors.firstName}
                showError={showErrors}
              />
              <TextInput
                label="Last name"
                value={draft.lastName}
                onChange={(v) => update('lastName', v)}
                required
                maxLength={NAME_MAX_LENGTH}
                error={errors.lastName}
                showError={showErrors}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectField
                label="Sex assigned at birth"
                value={draft.sex}
                onChange={(v) => {
                  // A manual pick means the field is no longer auto-seeded, so
                  // a later relationship change must not overwrite it.
                  autoSeededSexRef.current = null
                  update('sex', normalizeSexForDraft(v))
                }}
                options={SEX_OPTIONS}
                required
                error={errors.sex}
                showError={showErrors}
                placeholder="Select assigned sex at birth"
              />
              <TextInput
                label="Date of birth"
                value={draft.dateOfBirth}
                onChange={(v) => update('dateOfBirth', v)}
                type="date"
                max={localTodayIso()}
                required
                error={errors.dateOfBirth}
                showError={showErrors}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <TextInput
                      label="MRN"
                      value={draft.mrn}
                      onChange={(v) => update('mrn', v)}
                      required={mrnRequired}
                      maxLength={MRN_MAX_LENGTH}
                      error={errors.mrn}
                      showError={showErrors}
                    />
                  </div>
                  {showPatientLookup ? (
                    <span
                      className={patientLookupButtonTitle ? 'shrink-0 cursor-help' : 'shrink-0'}
                      title={patientLookupButtonTitle}
                    >
                      <button
                        type="button"
                        onClick={handleManualPatientLookup}
                        disabled={
                          !canRunPatientDemographicsLookup || !patientLookupAvailable || mrnLookupLoading
                        }
                        aria-label="Look up MRN from Epic"
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-base border border-border-base bg-white text-text-heading shadow-xs transition-colors hover:bg-background-gray disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
                      >
                        <Search className="h-4 w-4 shrink-0" aria-hidden />
                      </button>
                    </span>
                  ) : null}
                </div>
                {showPatientLookup && mrnLookupMessage ? (
                  <p
                    className="text-xs font-medium leading-5 text-text-body dark:text-gray-400"
                    role="status"
                  >
                    {mrnLookupMessage}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                label="Email"
                value={draft.email}
                onChange={(v) => update('email', v)}
                type="email"
                maxLength={EMAIL_MAX_LENGTH}
                icon={<Envelope className="h-4 w-4" aria-hidden />}
                required={contactChannelEnforced}
                ariaRequired={false}
                markerFilled={draft.email.trim() !== '' || draft.phone.trim() !== ''}
                error={errors.email}
                showError={showErrors}
                invalid={contactChannelInvalid}
                disabled={contactsDisabled}
                describedBy={showContactHelper ? contactHelperId : undefined}
              />
              <TextInput
                label="Phone"
                value={draft.phone}
                onChange={(v) => update('phone', v)}
                type="tel"
                maxLength={PHONE_MAX_LENGTH}
                icon={<Phone className="h-4 w-4" aria-hidden />}
                required={contactChannelEnforced}
                ariaRequired={false}
                markerFilled={draft.email.trim() !== '' || draft.phone.trim() !== ''}
                error={errors.phone}
                showError={showErrors}
                invalid={contactChannelInvalid}
                disabled={contactsDisabled}
                describedBy={showContactHelper ? contactHelperId : undefined}
              />
            </div>
            {showContactHelper ? (
              <p
                id={contactHelperId}
                className="text-xs font-medium leading-5 text-text-body-subtle dark:text-gray-500"
              >
                {MINOR_COMPARATOR_CONTACT_HELPER}
              </p>
            ) : null}

            {contactChannelInvalid && (
              <p
                className="text-xs leading-5 text-error-600 dark:text-error-100"
                role="alert"
                data-testid="comparator-contact-channel-error"
              >
                {primaryGuardianContactRequired
                  ? 'Enter a guardian email or phone number for research contact'
                  : ownResearchContactRequired
                    ? 'Enter an email or phone number for research contact'
                    : errors.contactChannel}
              </p>
            )}

            {guardianContactNeeded ? (
              <div
                className="flex flex-col items-start gap-2 text-xs leading-5 text-error-600 dark:text-error-100"
                data-testid="comparator-guardian-contact-needed"
              >
                <p role="alert">
                  This order needs a guardian email or phone number so consent to test can be collected.
                </p>
                {onRequestGuardianContact ? (
                  <button
                    type="button"
                    onClick={onRequestGuardianContact}
                    className="rounded-full border border-border-error-subtle px-3 py-1.5 text-xs font-medium hover:bg-error-50 dark:border-error-700 dark:hover:bg-gray-800"
                  >
                    Add guardian contact
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        )}

        {showTestingToggle && (
          <Toggle
            label="Provides a sample"
            description="Turn off for a contact who consents or is the legal guardian but is not being sequenced."
            checked={draft.isTestingComparator}
            onChange={(v) => update('isTestingComparator', v)}
            size="panel"
          />
        )}

        {showMultipleBirthBlock && isMultiple && draft.multipleBirth && (
          <div className="flex flex-col gap-4 rounded-base border border-border-base bg-background-gray/40 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-sm font-semibold leading-5 text-text-heading dark:text-white">
              Multiple-birth details
            </p>
            <SelectField
              label="Zygosity"
              value={draft.multipleBirth.zygosity}
              onChange={(v) => updateMultipleBirth('zygosity', v as Zygosity)}
              options={ZYGOSITY_OPTIONS}
            />
            <TextInput
              label="Birth order"
              value={draft.multipleBirth.birthOrder != null ? String(draft.multipleBirth.birthOrder) : ''}
              onChange={(v) => {
                const trimmed = v.trim()
                if (trimmed === '') {
                  updateMultipleBirth('birthOrder', null)
                  return
                }
                const n = Number.parseInt(trimmed, 10)
                if (Number.isFinite(n) && n > 0) {
                  updateMultipleBirth('birthOrder', n)
                }
              }}
              placeholder="e.g. 1, 2"
              enforceFreeText={false}
            />
            <p className="text-xs leading-5 text-text-body-subtle dark:text-gray-500">
              Sibling grouping (set ID) is assigned automatically when the order is saved.
            </p>
          </div>
        )}

        {showSampleTypeBlock && (
          <div className="flex items-stretch gap-2">
            <FieldRequiredRail
              state={resolveFieldMarkerState(
                draft.isTestingComparator && !lockSampleType,
                Boolean(draft.sampleType)
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col">
            <label className="mb-1.5 flex items-center text-sm font-medium text-text-heading dark:text-white">
              Sample type
              <FieldMarker
                state={resolveFieldMarkerState(
                  draft.isTestingComparator && !lockSampleType,
                  Boolean(draft.sampleType)
                )}
                fieldLabel="Sample type"
              />
            </label>
            {lockSampleType ? (
              <p
                className="text-sm text-text-body dark:text-gray-400"
                aria-label="Sample type"
                data-testid="comparator-sample-type-locked"
              >
                {draft.sampleType ? getSampleTypeLabel(draft.sampleType) : '—'}
              </p>
            ) : (
              <>
                <div className="flex w-full flex-row" role="radiogroup" aria-label="Sample type">
                  {availableSampleTypes.map((sampleType, index) => (
                    <RadioOption
                      key={sampleType}
                      name="comparator-sample-type"
                      value={sampleType}
                      checked={draft.sampleType === sampleType}
                      onChange={(value) =>
                        update('sampleType', value as SampleTypeOption)
                      }
                      label={getSampleTypeLabel(sampleType)}
                      first={index === 0}
                      last={index === availableSampleTypes.length - 1}
                      layout="inline"
                    />
                  ))}
                </div>
                {showErrors && errors.sampleType && (
                  <p className="mt-1.5 text-xs leading-5 text-text-fg-error dark:text-error-100">{errors.sampleType}</p>
                )}
              </>
            )}
            </div>
          </div>
        )}

        {showKitRequestBlock && (
          <>
            <Toggle
              label="Request collection kit"
              description="Please note, for Sample Type Blood, kit can only be shipped to site/clinic."
              checked={draft.requestKit}
              onChange={(v) => update('requestKit', v)}
              size="panel"
            />
            {draft.requestKit && (
              <div className="flex flex-col gap-4 rounded-base border border-border-base bg-background-gray/40 p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-sm font-semibold leading-5 text-text-heading dark:text-white">
                  Collection kit shipment
                </p>
                <SelectField
                  label="Destination"
                  value={draft.kitLocation}
                  onChange={handleKitLocationChange}
                  options={kitLocationOptions}
                  required
                  error={errors.kitLocation}
                  showError={showErrors}
                />
                <AddressAutocomplete
                  label="Street address"
                  value={draft.kitStreet}
                  onChange={(v) => update('kitStreet', v)}
                  onSelectAddress={applyResolvedKitAddress}
                  placeholder="123 Main St"
                  required={kitAddressRequired}
                  error={errors.kitStreet}
                  showError={showErrors}
                />
                <TextInput
                  label="Apt / suite"
                  value={draft.kitStreet2}
                  onChange={(v) => update('kitStreet2', v)}
                  placeholder="Apt 4"
                  enforceFreeText={false}
                />
                <TextInput
                  label="City"
                  value={draft.kitCity}
                  onChange={(v) => update('kitCity', v)}
                  enforceFreeText={false}
                  required={kitAddressRequired}
                  error={errors.kitCity}
                  showError={showErrors}
                />
                <TextInput
                  label="State"
                  value={draft.kitState}
                  onChange={(v) => update('kitState', v)}
                  enforceFreeText={false}
                  required={kitAddressRequired}
                  error={errors.kitState}
                  showError={showErrors}
                />
                <TextInput
                  label="ZIP"
                  value={draft.kitZip}
                  onChange={(v) => update('kitZip', v)}
                  enforceFreeText={false}
                  required={kitAddressRequired}
                  error={errors.kitZip}
                  showError={showErrors}
                />
              </div>
            )}
          </>
        )}

        {showClinicalBlock && (
          <div
            className={
              fieldGroups === 'full'
                ? 'flex flex-col gap-4 rounded-base border border-border-base bg-background-gray/40 p-4 dark:border-gray-700 dark:bg-gray-800'
                : 'flex flex-col gap-4'
            }
          >
            {fieldGroups === 'full' && (
              <p className="text-sm font-semibold leading-5 text-text-heading dark:text-white">
                Clinical details
              </p>
            )}
            <SelectField
              label="Affected Status"
              description="Is this relative affected by similar clinical conditions as the patient?"
              value={draft.phenotypicTrait}
              onChange={(v) => update('phenotypicTrait', normalizePhenotypicTraitForDraft(v))}
              options={PHENOTYPIC_TRAIT_OPTIONS}
            />
            <TextareaInput
              label="Comparator clinical summary"
              value={draft.clinicalNote}
              onChange={(v) => update('clinicalNote', v)}
              placeholder="Enter notes about this relation's clinical history that may be relevant to the patient's case"
              rows={5}
              error={errors.clinicalNote}
              showError={showErrors}
              helperText={clinicalNoteBudget.kind === 'shared' ? SHARED_CLINICAL_TEXT_HELPER : undefined}
              characterCount={clinicalNoteBudgetCount(draft.clinicalNote, clinicalNoteBudget)}
              characterLimit={clinicalNoteBudget.limit}
              characterCountOverLimit={clinicalNoteBudgetCount(draft.clinicalNote, clinicalNoteBudget) > clinicalNoteBudget.limit}
            />

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium leading-5 text-text-heading dark:text-white">
                Phenotypes
              </p>
              {draft.hpoTerms.length > 0 ? (
                <div className="flex flex-col border-t border-border-base dark:border-gray-700">
                  {displayHpoTerms.map((term) => (
                    <ComparatorPhenotypeRow
                      key={term.id}
                      term={term}
                      onTypeChange={changePhenotypeType}
                      onRemove={removePhenotype}
                      readOnly={suppressHpoEditing}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-base font-normal leading-6 text-text-body-subtle dark:text-gray-500">None entered</p>
              )}
              {suppressHpoEditing ? (
                <p className="text-sm text-text-body-subtle dark:text-gray-500">
                  Phenotypes are locked and cannot be modified for this member.
                </p>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  type="button"
                  onClick={() => setPhenotypeSearchOpen(true)}
                  className="self-start focus-visible:ring-brand-600"
                >
                  <Plus className="size-4" aria-hidden />
                  Add Phenotypes
                </Button>
              )}
            </div>
          </div>
        )}

        {showConsentBlock && comparatorConsentConfig && (
          <ComparatorParentConsentSections
            configs={comparatorConsentConfig}
            data={draftToFamilyMemberConsentView(draft)}
            probandIncidentalFindingsSelection={probandIncidentalFindingsSelection}
            showValidationErrors={showErrors}
            onIncidentalChange={(value: IncidentalFindingsSelection) =>
              update('includeIncidentalFindings', incidentalSelectionToCanonical(value))
            }
            onNyRetentionChange={(value: boolean) =>
              update('newYorkSampleRetentionOptIn', value)
            }
            onResearchContactChange={(value: boolean) =>
              update('researchContactConsent', value)
            }
          />
        )}

        {fieldGroups === 'consent' && !showConsentBlock && (
          <p className="text-sm text-text-body-subtle dark:text-gray-500">
            No additional options are available for this comparator.
          </p>
        )}

        {showClinicalBlock && !suppressHpoEditing && (
          <HPOSearchModal
            isOpen={phenotypeSearchOpen}
            onClose={() => setPhenotypeSearchOpen(false)}
            onSelect={addPhenotype}
            onRemove={removePhenotype}
            selectedTermIds={draft.hpoTerms.map((t) => t.id)}
            selectedTerms={draft.hpoTerms}
            showSelectedTerms={true}
            minQueryLength={3}
          />
        )}
      </div>
    </Modal>
  )
}
